import { useMemo, useEffect, useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ROUTES } from '../../api/routes';
import { AlertTriangle, ClipboardList, Mic, MicOff, Volume2, VolumeX, Shield, ShieldAlert, ArrowLeft, ThumbsUp, ThumbsDown, ChevronLeft, BookOpen, CheckCircle2, Play, Clock, ChevronRight, Users, X } from 'lucide-react';
import { toast } from 'sonner';
import { useVoiceAttendance } from '../../hooks/useVoiceAttendance';
import { useAuthStore } from '../../store/authStore';
import { useNavigate } from 'react-router-dom';
import { api, extractApiErrorMessage } from '../../api/client';
import {
  CollapsibleCard, STATUS_LABELS, toMinutes, fmtTimer, countAlunosNaTurma, alunoPertenceTurma, turmasComAlunosSugeridas,
  avaliarJanelaChamadaProfessor, turnoOperacionalDaAula,
} from '../../components/chamada';
import type { AulaStatus } from '../../components/chamada';
import type { Turma, StatusPresencaAula, HorarioAula, Aluno } from '../../types/models';
import { ChamadaProfessorDesktop } from '../professor/ChamadaProfessorDesktop';
import { DiarioClasseModal } from '../../components/pedagogico/DiarioClasseModal';
import type { CreateDiarioClasseInput } from '../../api/hooks/useDiarioClasse';
import { PRESENCAS } from '../../api/routes';
import { useHojeReferencia, useSimClock } from '../../api/hooks/useSimClock';
import { ChamadaBriefingMobile } from '../professor/chamada/ChamadaBriefingMobile';
import { ChamadaConfirmStart } from '../professor/chamada/ChamadaConfirmStart';
import { ChamadaJanelaModal, type JanelaModalData } from '../../components/chamada/ChamadaJanelaModal';
import { UserAvatar } from '../../components/ui/UserAvatar';
import {
  DIA_LABELS,
  formatDateSafe,
  mapAlunosChamadaParaPresencas,
} from '../professor/chamada/chamada-professor-shared';

interface ChamadaProfessorProps {
  user: any;
  aulaAgora: HorarioAula | null;
  proximaAula: HorarioAula | null;
  aulasHoje: HorarioAula[];
  statusMap: Record<string, { status: string; totalPresentes: number; totalAusentes: number; totalAlunos?: number }>;
  turmas: Turma[];
  alunosTurma: any[];
  materiaAulaAgora: string;
  turmaAulaAgora: Turma | undefined;
  selectedSlot: HorarioAula | null;
  setSelectedSlot: (s: HorarioAula | null) => void;
  presencas: Record<string, StatusPresencaAula>;
  setPresencas: (p: Record<string, StatusPresencaAula> | ((prev: Record<string, StatusPresencaAula>) => Record<string, StatusPresencaAula>)) => void;
  faseAtual: 'briefing' | 'operacao' | 'resumo';
  setFaseAtual: (f: 'briefing' | 'operacao' | 'resumo') => void;
  alunoIdx: number;
  setAlunoIdx: (i: number) => void;
  chamadaId: string | null;
  setChamadaId: (id: string | null) => void;
  rfidMap: Record<string, { entrou: boolean; horaEntrada?: string }>;
  setRfidMap: (m: Record<string, { entrou: boolean; horaEntrada?: string }>) => void;
  dataAtual: string;
  setDataAtual?: (d: string) => void;
  diaSemana: string;
  agoraSegundos: number;
  restante: number;
  progresso: number;
  chamadasDia: any[];
  handleSalvar: () => Promise<void>;
  saving: boolean;
  salvarProgressoLocal: (extras?: any) => void;
  limparProgressoLocal: () => void;
  CHAMADA_STORAGE_KEY: string;
  presentes: number;
  faltas: number;
  atrasados: number;
  getAulaStatus: (slot: HorarioAula) => AulaStatus;
  dialogPerdida: HorarioAula | null;
  setDialogPerdida: (h: HorarioAula | null) => void;
  cancelDialog: boolean;
  setCancelDialog: (v: boolean) => void;
  loadingAlunos?: boolean;
  todosAlunos?: any[];
  /** Contagem por turmaId (fluxo professor — evita carregar todos os alunos da escola) */
  contagemPorTurma?: Record<string, number>;
}

export function ChamadaProfessor(props: ChamadaProfessorProps) {
  const {
    user, aulaAgora, proximaAula, aulasHoje, statusMap, turmas, alunosTurma,
    materiaAulaAgora, turmaAulaAgora, selectedSlot, setSelectedSlot,
    presencas, setPresencas, faseAtual, setFaseAtual, alunoIdx, setAlunoIdx,
    chamadaId, setChamadaId, rfidMap, setRfidMap, dataAtual, setDataAtual, diaSemana,
    agoraSegundos, restante, progresso, chamadasDia, handleSalvar, saving,
    salvarProgressoLocal, limparProgressoLocal, CHAMADA_STORAGE_KEY,
    presentes, faltas, atrasados, getAulaStatus,
    dialogPerdida, setDialogPerdida, cancelDialog, setCancelDialog,
    loadingAlunos, todosAlunos = [], contagemPorTurma,
  } = props;

  const queryClient = useQueryClient();
  const { hojeStr } = useHojeReferencia();
  const { virtualEpochMs, isSimulation } = useSimClock();
  const agoraOperacional = () =>
    isSimulation ? new Date(virtualEpochMs) : new Date();
  const [iniciandoChamada, setIniciandoChamada] = useState(false);
  const [confirmAula, setConfirmAula] = useState<HorarioAula | null>(null);
  const [janelaModalData, setJanelaModalData] = useState<JanelaModalData | null>(null);
  const [loadedAlunos, setLoadedAlunos] = useState<Aluno[]>([]);

  const countAlunosTurma = (turmaId: string) => {
    if (loadedAlunos.length > 0 && selectedSlot?.turmaId === turmaId) {
      return loadedAlunos.length;
    }
    if (contagemPorTurma) {
      return contagemPorTurma[turmaId] ?? 0;
    }
    return countAlunosNaTurma(todosAlunos, turmaId);
  };

  // ── NavigationBlocker: impedir navegacao lateral durante chamada ativa ──
  // React Router v7 useBlocker - bloqueia navegacao durante operacao/resumo
  const shouldBlock = faseAtual === 'operacao' || faseAtual === 'resumo';
  useEffect(() => {
    if (!shouldBlock) return;
    // Feedback visual ao tentar navegar (complementa o beforeunload existente)
    const handler = () => {
      if (shouldBlock) {
        const confirmLeave = window.confirm('Chamada em andamento! Deseja realmente sair? Seu progresso sera salvo localmente.');
        if (!confirmLeave) {
          // Re-push current state para cancelar a navegacao
          window.history.pushState(null, '', window.location.href);
        } else {
          salvarProgressoLocal();
        }
      }
    };
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, [shouldBlock, salvarProgressoLocal]);

  const aulaJaFeita = Boolean(
    aulaAgora &&
      (() => {
        const s = statusMap[aulaAgora.id];
        const st = String(s?.status || '').toUpperCase();
        return st === 'CONCLUIDA' || (st === 'ABERTA' && (s?.totalAlunos ?? 0) > 0);
      })(),
  );

  const requestStartChamada = useCallback((aula: HorarioAula) => {
    if (iniciandoChamada) return;
    setConfirmAula(aula);
  }, [iniciandoChamada]);

  // ── Detail modal for viewing completed attendance ──
  const [detailSlot, setDetailSlot] = useState<HorarioAula | null>(null);
  const [detailData, setDetailData] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showDiarioModal, setShowDiarioModal] = useState(false);
  const [salvandoDiario, setSalvandoDiario] = useState(false);
  const escolaIdStore = useAuthStore((s) => s.escolaId);
  const currentUser = useAuthStore((s) => s.user);

  const openAulaDetail = useCallback(async (slot: HorarioAula) => {
    const chamadaInfo = chamadasDia.find((c: any) => c.horarioAulaId === slot.id && c.status === 'CONCLUIDA');
    if (!chamadaInfo || !escolaIdStore) return;
    setDetailSlot(slot);
    setDetailData(null);
    setDetailLoading(true);
    try {
      const { data } = await api.get(ROUTES.chamadas.findOne(escolaIdStore, chamadaInfo.id));
      setDetailData(data);
    } catch (err) {
      console.error('Erro ao carregar detalhes da chamada:', err);
      // Fallback: use basic data from statusMap
      setDetailData({ _fallback: true, totalPresentes: chamadaInfo.totalPresentes, totalAusentes: chamadaInfo.totalAusentes });
    } finally {
      setDetailLoading(false);
    }
  }, [chamadasDia, escolaIdStore]);

  // ── Sincronizacao de presencas entre abas/janelas (Cross-Tab Sync) ──
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === CHAMADA_STORAGE_KEY && e.newValue) {
        try {
          const remoteState = JSON.parse(e.newValue);
          if (remoteState.chamadaId === chamadaId && remoteState.presencas) {
            setPresencas((localPresencas) => {
              if (JSON.stringify(localPresencas) !== JSON.stringify(remoteState.presencas)) {
                return remoteState.presencas;
              }
              return localPresencas;
            });
          }
        } catch {
          // Parse silencioso
        }
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [chamadaId]);

  // Sincronizar indice de alunos quando a turma mudar
  const todosAlunosDaTurma = useMemo(() => {
    if (loadedAlunos && loadedAlunos.length > 0) return loadedAlunos;
    if (todosAlunos && todosAlunos.length > 0) return todosAlunos;
    return alunosTurma || [];
  }, [loadedAlunos, todosAlunos, alunosTurma]);

  const alunosTurmaOrdenados = useMemo(() => {
    const turmaIdAtiva = selectedSlot?.turmaId || aulaAgora?.turmaId;
    if (!turmaIdAtiva) return [];
    return todosAlunosDaTurma
      .filter((a: any) => !a.turmaId || a.turmaId === turmaIdAtiva)
      .sort((a: any, b: any) => (a.nome || '').localeCompare(b.nome || ''));
  }, [todosAlunosDaTurma, selectedSlot?.turmaId, aulaAgora?.turmaId]);

  const alunosChamada = alunosTurmaOrdenados;
  const alunoCard = alunosChamada[alunoIdx];
  const totalAlunos = alunosChamada.length;
  const respondidos = Object.keys(presencas).length;
  const todosRespondidos = respondidos >= totalAlunos && totalAlunos > 0;

  const alunosPendentes = useMemo(() => {
    return alunosTurmaOrdenados.filter((a: any) => !presencas[a.id]);
  }, [alunosTurmaOrdenados, presencas]);

  const alunosChamados = useMemo(() => {
    return alunosTurmaOrdenados.filter((a: any) => !!presencas[a.id]);
  }, [alunosTurmaOrdenados, presencas]);

  // Auto-correcao do indice de alunos (auto-healing) para evitar out-of-bounds apos restauracao de cache ou troca de turmas
  useEffect(() => {
    if (alunosTurmaOrdenados.length > 0 && alunoIdx >= alunosTurmaOrdenados.length) {
      setAlunoIdx(0);
    }
  }, [alunosTurmaOrdenados, alunoIdx, setAlunoIdx]);

  // helpers
  // Aula-alvo: pode ser aulaAgora OU selectedSlot (se abriu antecipadamente)
  const aulaAlvo = selectedSlot || aulaAgora;

  const iniciarChamada = async (aulaOverride?: HorarioAula, opts?: { foraDoHorario?: boolean }) => {
    const aula = aulaOverride || aulaAlvo;
    if (!aula || iniciandoChamada) return;
    const escolaId = useAuthStore.getState().escolaId;
    if (!escolaId) { toast.error('Escola nao identificada'); return; }

    const agora = agoraOperacional();
    const horaAtualStr = `${String(agora.getHours()).padStart(2, '0')}:${String(agora.getMinutes()).padStart(2, '0')}`;

    // ── Validação de horário: não permitir iniciar chamada antes do início da aula ──
    // Janela de tolerância: 5 minutos antes do início
    if (!opts?.foraDoHorario && aula.horaInicio) {
      const minAtual = toMinutes(horaAtualStr);
      const minInicio = toMinutes(aula.horaInicio);
      const minAntesDoInicio = minInicio - minAtual;
      if (minAntesDoInicio > 5) {
        setJanelaModalData({
          isOpen: true,
          tipo: 'antecedencia',
          aula,
          turmaNome: turmas.find((t) => t.id === aula.turmaId)?.nome,
          dataReferencia: dataAtual,
          horaAtual: horaAtualStr,
          limiteFormatado: aula.horaInicio,
          mensagem: `A aula inicia às ${aula.horaInicio} (faltam ${minAntesDoInicio} min). Aguarde o início para abrir a chamada.`,
          onClose: () => setJanelaModalData(null),
        });
        return;
      }
    }

    // ── Validação de data: não vigente (passada ou futura) ──
    if (dataAtual && hojeStr && dataAtual !== hojeStr && !isSimulation) {
      const isPassado = dataAtual < hojeStr;
      const [y, m, d] = dataAtual.split('-');
      if (!opts?.foraDoHorario) {
        if (isPassado) {
          setJanelaModalData({
            isOpen: true,
            tipo: 'data_diferente',
            aula,
            turmaNome: turmas.find((t) => t.id === aula.turmaId)?.nome,
            dataReferencia: dataAtual,
            horaAtual: horaAtualStr,
            mensagem: `A aula selecionada é do dia ${d}/${m}/${y} (data retroativa). A chamada será realizada fora do horário regular e auditada pela Coordenação.`,
            onClose: () => setJanelaModalData(null),
            onProceed: () => {
              setJanelaModalData(null);
              void iniciarChamada(aula, { foraDoHorario: true });
            },
            proceedLabel: 'Fazer Chamada com Justificativa',
          });
          return;
        } else {
          setJanelaModalData({
            isOpen: true,
            tipo: 'data_diferente',
            aula,
            turmaNome: turmas.find((t) => t.id === aula.turmaId)?.nome,
            dataReferencia: dataAtual,
            horaAtual: horaAtualStr,
            mensagem: `A aula selecionada é de uma data futura (${d}/${m}/${y}). A chamada só pode ser realizada a partir do dia da aula.`,
            onClose: () => setJanelaModalData(null),
          });
          return;
        }
      }
    }

    const turnoOp = turnoOperacionalDaAula(aula, turmas);
    const tolerancia = turnoOp?.toleranciaChamadaMin ?? 10;
    const janelaExtra = turnoOp?.janelaExtraChamadaMin ?? 60;
    const janela = avaliarJanelaChamadaProfessor(aula.horaInicio, aula.horaFim, turnoOp, {
      foraDoHorario: opts?.foraDoHorario,
      agora: agoraOperacional(),
    });
    // Em simulação a fila/cockpit pode recuperar após a janela (espelha backend).
    if (!janela.ok && !(isSimulation && /expirou/i.test(janela.message))) {
      if (!opts?.foraDoHorario) {
        setJanelaModalData({
          isOpen: true,
          tipo: 'expirou',
          aula,
          turmaNome: turmas.find((t) => t.id === aula.turmaId)?.nome,
          dataReferencia: dataAtual,
          horaAtual: horaAtualStr,
          limiteFormatado: janela.limite,
          toleranciaMin: tolerancia,
          janelaExtraMin: janelaExtra,
          mensagem: `A janela padrão desta aula encerrou às ${janela.limite}. A chamada será registrada fora do horário da grade.`,
          onClose: () => setJanelaModalData(null),
          onProceed: () => {
            setJanelaModalData(null);
            void iniciarChamada(aula, { foraDoHorario: true });
          },
          proceedLabel: 'Fazer Chamada com Justificativa',
        });
        return;
      }
    }

      setIniciandoChamada(true);
      try {
        // RFID + status-dia + alunos em paralelo (sem bloqueio prematuro de cache)
        const alunosKey = ['chamada-alunos-turma', escolaId, aula.turmaId] as const;
        const [rfidResult, statusResult, alunosPrefetch] = await Promise.all([
          api
            .get(PRESENCAS.diarias(escolaId, {}), {
              params: { turmaId: aula.turmaId, dataReferencia: dataAtual, limite: 200 },
            })
            .then((r) => r.data)
            .catch(() => null),
          api
            .get(ROUTES.chamadas.statusDia(escolaId), {
              params: { professorId: user?.id, data: dataAtual },
            })
            .then((r) => r.data)
            .catch((err: { response?: { status?: number }; message?: string }) => {
              console.warn('[Chamada] Falha ao buscar status-dia:', err?.response?.status, err?.message);
              return null;
            }),
          queryClient
            .fetchQuery({
              queryKey: alunosKey,
              queryFn: async () => {
                const { data } = await api.get<{ turma?: any; alunos: Aluno[] }>(
                  `/escolas/${escolaId}/chamadas/turma/${aula.turmaId}/alunos`,
                );
                return (data?.alunos ?? []) as Aluno[];
              },
              staleTime: 60_000,
            })
            .catch(() => [] as Aluno[]),
        ]);

        if (Array.isArray(alunosPrefetch) && alunosPrefetch.length > 0) {
          setLoadedAlunos(alunosPrefetch);
        } else if (countAlunosTurma(aula.turmaId) === 0) {
          toast.error('Não é possível iniciar: esta turma não possui alunos cadastrados.');
          return;
        }

        const map: Record<string, { entrou: boolean; horaEntrada?: string }> = {};
        const items = rfidResult?.data || rfidResult || [];
        if (Array.isArray(items)) {
          items.forEach((p: { alunoId: string; entrouNaEscola?: boolean; primeiroEventoEntradaEm?: string }) => {
            map[p.alunoId] = {
              entrou: !!p.entrouNaEscola,
              horaEntrada: p.primeiroEventoEntradaEm
                ? new Date(p.primeiroEventoEntradaEm).toLocaleTimeString('pt-BR', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })
                : undefined,
            };
          });
        }
        setRfidMap(map);

        let chamadaExistenteId: string | null = null;
        let chamadaJaConcluida = false;
        const lista = Array.isArray(statusResult) ? statusResult : [];
        const existente = lista.find(
          (c: {
            horarioAulaId?: string;
            turmaId?: string;
            materiaId?: string;
            numeroAula?: number | null;
            status?: string;
            id?: string;
          }) =>
            c.horarioAulaId === aula.id
            || (!c.horarioAulaId && c.turmaId === aula.turmaId && c.materiaId === aula.materiaId)
            || (!c.horarioAulaId &&
              c.turmaId === aula.turmaId &&
              c.numeroAula != null &&
              c.numeroAula === aula.numeroAula),
        );
        if (existente) {
          if (existente.status === 'CONCLUIDA') chamadaJaConcluida = true;
          else if (existente.status === 'ABERTA') chamadaExistenteId = existente.id || null;
        }

        if (chamadaJaConcluida) {
          toast.info('Chamada já foi realizada para esta aula.');
          return;
        }

        let newChamadaId: string;
        let seedPresencas: Record<string, StatusPresencaAula> = {};
        if (chamadaExistenteId) {
          newChamadaId = chamadaExistenteId;
          try {
            const { data: detalhe } = await api.get(ROUTES.chamadas.findOne(escolaId, newChamadaId));
            seedPresencas = mapAlunosChamadaParaPresencas(detalhe);
          } catch {
            seedPresencas = {};
          }
          toast.info('Chamada em andamento restaurada.');
        } else {
          const { data: chamadaCriada } = await api.post(ROUTES.chamadas.listar(escolaId), {
            turmaId: aula.turmaId,
            professorId: user?.id,
            materiaId: aula.materiaId || undefined,
            horarioAulaId: aula.id || undefined,
            dataReferencia: dataAtual,
            ...(opts?.foraDoHorario ? { foraDoHorario: true } : {}),
          });
          newChamadaId = chamadaCriada?.id;
          if (!newChamadaId) { toast.error('Erro ao criar chamada'); return; }
          seedPresencas = mapAlunosChamadaParaPresencas(chamadaCriada);
          const nJust = Object.values(seedPresencas).filter((s) => s === 'JUSTIFICADO').length;
          toast.success(
            nJust > 0
              ? `Chamada iniciada · ${nJust} aluno(s) já justificado(s)`
              : 'Chamada iniciada!',
          );
        }

        // Só troca de fase com alunos já no cache + chamada pronta.
        if (Array.isArray(alunosPrefetch) && alunosPrefetch.length > 0) {
          setLoadedAlunos(alunosPrefetch);
        }
        setChamadaId(newChamadaId);
        setSelectedSlot(aula);
        setPresencas(seedPresencas);
        setAlunoIdx(0);
        setFaseAtual('operacao');

        localStorage.setItem(CHAMADA_STORAGE_KEY, JSON.stringify({
          chamadaId: newChamadaId,
          slotId: aula.id || '',
          presencas: seedPresencas,
          alunoIdx: 0,
          faseAtual: 'operacao',
          timestamp: Date.now(),
        }));
      } catch (err: unknown) {
        const e = err as { response?: { status?: number; data?: any }; message?: string };
        console.error('[Chamada] Erro ao iniciar:', e?.response?.status, e?.response?.data);
        const errorMsg = extractApiErrorMessage(err, 'Erro ao iniciar chamada');
        if (/expirou|janela|retroativ|passad|futur|limite/i.test(errorMsg)) {
          const agora = agoraOperacional();
          const horaAtualStr = `${String(agora.getHours()).padStart(2, '0')}:${String(agora.getMinutes()).padStart(2, '0')}`;
          const turnoOp = turnoOperacionalDaAula(aula, turmas);
          const tolerancia = turnoOp?.toleranciaChamadaMin ?? 10;
          const janelaExtra = turnoOp?.janelaExtraChamadaMin ?? 60;
          setJanelaModalData({
            isOpen: true,
            tipo: /passad|retroativ|futur/i.test(errorMsg) ? 'data_diferente' : 'expirou',
            aula,
            turmaNome: turmas.find((t) => t.id === aula.turmaId)?.nome,
            dataReferencia: dataAtual,
            horaAtual: horaAtualStr,
            toleranciaMin: tolerancia,
            janelaExtraMin: janelaExtra,
            mensagem: errorMsg,
            onClose: () => setJanelaModalData(null),
          });
        } else {
          toast.error(errorMsg);
        }
      } finally {
        setIniciandoChamada(false);
      }
    };

    // Restaurar sessao do localStorage ao montar (se houver)
     
    useEffect(() => {
      // GUARD: Nao rodar enquanto aulasHoje nao carregou — evita limpar localStorage prematuramente
      if (aulasHoje.length === 0) return;

      try {
        const raw = localStorage.getItem(CHAMADA_STORAGE_KEY);
        if (!raw) {
          // Sem sessao local, verificar se tem chamada ABERTA no banco
          if (aulaAgora && statusMap[aulaAgora.id]?.status !== 'CONCLUIDA' && statusMap[aulaAgora.id]) {
            // Chamada existe e NAO esta concluida - restaurar sessao do banco
            const chamadaDoBanco = chamadasDia.find(c => c.horarioAulaId === aulaAgora.id && c.status !== 'CONCLUIDA');
            if (chamadaDoBanco) {
              setChamadaId(chamadaDoBanco.id);
              setSelectedSlot(aulaAgora);
              setFaseAtual('briefing'); // volta pro briefing para o professor ver o botao "Continuar"
              toast.info('Chamada em andamento detectada. Clique em Continuar.');
            }
          }
          return;
        }
        const dados = JSON.parse(raw);
        if (!dados.chamadaId || !dados.slotId) { limparProgressoLocal(); return; }

        // Verificar se o slot ainda existe nas aulas de hoje
        const slot = aulasHoje.find(a => a.id === dados.slotId);
        if (!slot) {
          // Slot nao encontrado — mas aulasHoje ja carregou (guard acima), entao e' realmente invalido
          limparProgressoLocal();
          return;
        }

        // Verificar se a chamada ja foi concluida por outro meio (ex: admin concluiu)
        if (statusMap[slot.id]?.status === 'CONCLUIDA') {
          limparProgressoLocal();
          toast.info('Esta chamada ja foi concluida.');
          return;
        }

        // Restaurar estado + mesclar JUSTIFICADO do servidor (abonos)
        (async () => {
          let merged: Record<string, StatusPresencaAula> = { ...(dados.presencas || {}) };
          if (escolaIdStore && dados.chamadaId) {
            try {
              const { data: detalhe } = await api.get(
                ROUTES.chamadas.findOne(escolaIdStore, dados.chamadaId),
              );
              merged = { ...mapAlunosChamadaParaPresencas(detalhe), ...merged };
              // Abono do servidor prevalece
              for (const [id, st] of Object.entries(mapAlunosChamadaParaPresencas(detalhe))) {
                if (st === 'JUSTIFICADO') merged[id] = 'JUSTIFICADO';
              }
            } catch {
              /* mantém local */
            }
          }
          setChamadaId(dados.chamadaId);
          setSelectedSlot(slot);
          setPresencas(merged);
          setAlunoIdx(dados.alunoIdx || 0);
          setFaseAtual(dados.faseAtual || 'operacao');
          const respostas = Object.keys(merged).length;
          if (respostas > 0) {
            toast.success(`Chamada restaurada! ${respostas} alunos ja marcados.`);
          } else {
            toast.info('Chamada em andamento restaurada.');
          }
        })();
      } catch {
        limparProgressoLocal();
      }
    }, [aulasHoje.length]); // roda quando aulasHoje carregam

    const marcarEAvancar = (alunoId: string, status: StatusPresencaAula) => {
      if (presencas[alunoId] === 'JUSTIFICADO' && status !== 'JUSTIFICADO') {
        toast.info('Falta já abonada pela escola — mantido como Justificado.');
        return;
      }
      const novasPresencas = { ...presencas, [alunoId]: status };
      setPresencas(novasPresencas);

      // Persistir no localStorage a cada marcacao
      const dados = {
        chamadaId,
        slotId: selectedSlot?.id || '',
        presencas: novasPresencas,
        alunoIdx,
        faseAtual: 'operacao',
        timestamp: Date.now(),
      };
      localStorage.setItem(CHAMADA_STORAGE_KEY, JSON.stringify(dados));

      // auto-avanco com delay
      setTimeout(() => {
        // encontrar proximo nao-respondido (para frente)
        const nextIdx = alunosTurmaOrdenados.findIndex((a, i) => i > alunoIdx && !novasPresencas[a.id]);
        if (nextIdx >= 0) {
          setAlunoIdx(nextIdx);
        } else {
          // Wrap-around: procurar desde o inicio da lista (alunos pulados)
          const wrapIdx = alunosTurmaOrdenados.findIndex((a) => !novasPresencas[a.id]);
          if (wrapIdx >= 0) setAlunoIdx(wrapIdx);
          else setFaseAtual('resumo'); // todos respondidos
        }
      }, 300);
    };

    // Auto-scroll: quando alunoIdx muda, rolar o item selecionado para ficar visivel
    useEffect(() => {
      const timer = setTimeout(() => {
        const el = document.querySelector(`[data-aluno-idx="${alunoIdx}"]`) as HTMLElement;
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 60);
      return () => clearTimeout(timer);
    }, [alunoIdx, presencas]);

    // -- Feedback sonoro (beep) --
    const playBeep = useCallback((freq: number, duration: number) => {
      try {
        const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = freq;
        osc.type = 'sine';
        gain.gain.value = 0.15;
        osc.start();
        osc.stop(ctx.currentTime + duration / 1000);
        setTimeout(() => ctx.close(), duration + 100);
      } catch { /* AudioContext nao disponivel */ }
    }, []);

    // -- Atalhos de teclado --
    useEffect(() => {
      if (faseAtual !== 'operacao' || !alunosTurmaOrdenados.length) return;
      const handler = (e: KeyboardEvent) => {
        // Ignorar se estiver num input/textarea
        const tag = (e.target as HTMLElement)?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

        const aluno = alunosTurmaOrdenados[alunoIdx];
        if (!aluno) return;

        switch (e.key.toLowerCase()) {
          case 'p':
            e.preventDefault();
            playBeep(880, 80); // beep agudo = presente
            marcarEAvancar(aluno.id, 'PRESENTE');
            break;
          case 'a':
            e.preventDefault();
            playBeep(330, 120); // beep grave = ausente
            marcarEAvancar(aluno.id, 'AUSENTE');
            break;
          case 'j':
            e.preventDefault();
            playBeep(660, 100);
            marcarEAvancar(aluno.id, 'JUSTIFICADO');
            break;
          case 'd':
            e.preventDefault();
            playBeep(660, 100);
            marcarEAvancar(aluno.id, 'DISPENSADO');
            break;
          case 't':
            e.preventDefault();
            playBeep(550, 100);
            marcarEAvancar(aluno.id, 'ATRASADO');
            break;
          case 'arrowright':
          case 'arrowdown': {
            e.preventDefault();
            const nextIdx = alunosTurmaOrdenados.findIndex((a, i) => i > alunoIdx && !presencas[a.id]);
            if (nextIdx >= 0) setAlunoIdx(nextIdx);
            else {
              const wrapIdx = alunosTurmaOrdenados.findIndex(a => !presencas[a.id]);
              if (wrapIdx >= 0) setAlunoIdx(wrapIdx);
            }
            break;
          }
          case 'arrowleft':
          case 'arrowup':
            e.preventDefault();
            setAlunoIdx(Math.max(0, alunoIdx - 1));
            break;
          case 'enter':
            if (todosRespondidos) { e.preventDefault(); setFaseAtual('resumo'); }
            break;
        }
      };
      window.addEventListener('keydown', handler);
      return () => window.removeEventListener('keydown', handler);
    }, [faseAtual, alunoIdx, alunosTurmaOrdenados, presencas, todosRespondidos, playBeep]);

    const cancelarChamada = () => {
      // Manter progresso no localStorage (pode ser restaurado depois)
      salvarProgressoLocal();
      setFaseAtual('briefing');
      setCancelDialog(false);
    };

    const getIniciais = (nome: string) => nome.split(' ').filter(Boolean).slice(0, 2).map(n => n[0]).join('').toUpperCase();

    // Smart display names: primeiro nome se único; se repetido, primeiro nome + próximo sobrenome (parts[1])
    const displayNames = useMemo(() => {
      const map: Record<string, string> = {};
      const firstNames: Record<string, string[]> = {};
      alunosTurmaOrdenados.forEach((a: any) => {
        const parts = (a.nome || '').trim().split(/\s+/).filter(Boolean);
        const first = parts[0] || '';
        if (!firstNames[first]) firstNames[first] = [];
        firstNames[first].push(a.id);
      });
      alunosTurmaOrdenados.forEach((a: any) => {
        const parts = (a.nome || '').trim().split(/\s+/).filter(Boolean);
        const first = parts[0] || '';
        if (firstNames[first]?.length > 1 && parts.length > 1) {
          map[a.id] = `${first} ${parts[1]}`;
        } else {
          map[a.id] = first;
        }
      });
      return map;
    }, [alunosTurmaOrdenados]);

    // â”€â”€ Chamada por Voz â”€â”€
    const STATUS_PT: Record<string, string> = {
      PRESENTE: 'presente', AUSENTE: 'ausente', ATRASADO: 'atrasado',
      JUSTIFICADO: 'justificado', DISPENSADO: 'dispensado',
    };

    // Encontrar aluno por primeiro nome (fuzzy match)
    const findAlunoByName = useCallback((spokenName: string): number => {
      const spoken = spokenName.toLowerCase().trim();
      if (!spoken) return -1;

      // 1) Match exato no displayName (ex: "gabriel silva" == "Gabriel Silva")
      for (let i = 0; i < alunosTurmaOrdenados.length; i++) {
        const dn = (displayNames[alunosTurmaOrdenados[i].id] || '').toLowerCase();
        if (dn === spoken) return i;
      }

      // 2) Match exato no nome completo (ex: "gabriel silva santos")
      for (let i = 0; i < alunosTurmaOrdenados.length; i++) {
        if (alunosTurmaOrdenados[i].nome.toLowerCase() === spoken) return i;
      }

      // 3) Match pelo primeiro nome (ex: "gabriel")
      for (let i = 0; i < alunosTurmaOrdenados.length; i++) {
        const firstName = alunosTurmaOrdenados[i].nome.split(' ')[0].toLowerCase();
        if (firstName === spoken) return i;
      }

      // 4) Match pelo sobrenome/último nome (ex: "silva" ou "santos")
      for (let i = 0; i < alunosTurmaOrdenados.length; i++) {
        const parts = alunosTurmaOrdenados[i].nome.toLowerCase().split(' ').filter(Boolean);
        if (parts.length > 1) {
          const lastName = parts[parts.length - 1];
          if (lastName === spoken) return i;
          // Verificar se falou "nome sobrenome" parcial (ex: "gabriel silva")
          const fullLower = alunosTurmaOrdenados[i].nome.toLowerCase();
          if (fullLower.startsWith(spoken) || fullLower.includes(spoken)) return i;
        }
      }

      // 5) Match parcial (qualquer parte do nome contém a busca)
      for (let i = 0; i < alunosTurmaOrdenados.length; i++) {
        const nomeLower = alunosTurmaOrdenados[i].nome.toLowerCase();
        const dnLower = (displayNames[alunosTurmaOrdenados[i].id] || '').toLowerCase();
        if (nomeLower.includes(spoken) || dnLower.includes(spoken)) return i;
      }

      return -1;
    }, [alunosTurmaOrdenados, displayNames]);

    const voiceEventHandler = useCallback((event: import('../../hooks/useVoiceAttendance').VoiceEvent) => {
      if (faseAtual !== 'operacao' || !alunosTurmaOrdenados.length) return;

      if (event.type === 'status') {
        // Comando simples: marca aluno atual
        const aluno = alunosTurmaOrdenados[alunoIdx];
        if (aluno) marcarEAvancar(aluno.id, event.status);

      } else if (event.type === 'navigate') {
        if (event.direction === 'next') {
          // Próximo não-respondido
          const nextIdx = alunosTurmaOrdenados.findIndex((a, i) => i > alunoIdx && !presencas[a.id]);
          if (nextIdx >= 0) setAlunoIdx(nextIdx);
          else {
            const wrapIdx = alunosTurmaOrdenados.findIndex(a => !presencas[a.id]);
            if (wrapIdx >= 0) setAlunoIdx(wrapIdx);
          }
        } else {
          // Voltar
          const prevIdx = Math.max(0, alunoIdx - 1);
          setAlunoIdx(prevIdx);
        }

      } else if (event.type === 'nameStatus') {
        // "CLARA PRESENTE" ou "GABRIEL SILVA PRESENTE" â†’ encontra, navega, marca
        const idx = findAlunoByName(event.name);
        if (idx >= 0) {
          const aluno = alunosTurmaOrdenados[idx];
          setAlunoIdx(idx);
          setTimeout(() => marcarEAvancar(aluno.id, event.status), 150);
        }

      } else if (event.type === 'nameOnly') {
        // "GABRIEL SILVA" ou "MARIA CLARA" â†’ só navega (sem marcar)
        const idx = findAlunoByName(event.name);
        if (idx >= 0) {
          setAlunoIdx(idx);
        }
      }
    }, [faseAtual, alunosTurmaOrdenados, alunoIdx, presencas, marcarEAvancar, findAlunoByName]);

    // Carregar preferências salvas do usuário
    const updatePreferencias = useAuthStore((s) => s.updatePreferencias);
    const chamadaPrefs = user?.preferencias?.chamada || {};

    const { micActive, toggleMic, lastHeard, ttsActive, toggleTts, speakText } = useVoiceAttendance(
      voiceEventHandler,
      { micActive: chamadaPrefs.micAtivo ?? false, ttsActive: chamadaPrefs.ttsAtivo ?? false },
    );

    // Persistir preferências quando mic/TTS mudam
    const handleToggleMic = useCallback(() => {
      toggleMic();
      // Salvar no backend (debounced via setTimeout)
      setTimeout(() => {
        const newState = !micActive;
        updatePreferencias({ chamada: { micAtivo: newState } });
      }, 100);
    }, [toggleMic, micActive, updatePreferencias]);

    const handleToggleTts = useCallback(() => {
      toggleTts();
      setTimeout(() => {
        const newState = !ttsActive;
        updatePreferencias({ chamada: { ttsAtivo: newState } });
      }, 100);
    }, [toggleTts, ttsActive, updatePreferencias]);

    // TTS: falar nome quando muda de aluno (se TTS ativo)
    // Se aluno já tem status, anuncia "NOME STATUS"
    useEffect(() => {
      if (!ttsActive || faseAtual !== 'operacao' || !alunosTurmaOrdenados[alunoIdx]) return;

      const aluno = alunosTurmaOrdenados[alunoIdx];
      const nome = displayNames[aluno.id] || aluno.nome;
      const statusAtual = presencas[aluno.id];

      const timer = setTimeout(() => {
        if (statusAtual) {
          // Aluno já marcado â†’ anuncia nome + status
          speakText(`${nome}, ${STATUS_PT[statusAtual] || statusAtual}`);
        } else {
          // Aluno sem status â†’ fala só o nome
          speakText(nome);
        }
      }, 400);

      return () => clearTimeout(timer);
    }, [alunoIdx, ttsActive, faseAtual]);

    // Auto-absent desativado por enquanto
    // TODO: Reativar com lógica de repetição vinculada ao botão TTS
    // const scheduleRetry = ...



    const navigate = useNavigate();
    const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' ? window.innerWidth < 768 : false);
    useEffect(() => {
      const fn = () => setIsMobile(window.innerWidth < 768);
      window.addEventListener('resize', fn);
      return () => window.removeEventListener('resize', fn);
    }, []);

    // Count pendencias for tab bar badge
    const totalPerdidas = aulasHoje.filter(a => getAulaStatus(a) === 'perdida').length;

    return (
      <>
      {isMobile && faseAtual === 'briefing' && (
        <ChamadaBriefingMobile
          dataAtual={dataAtual}
          setDataAtual={setDataAtual}
          hojeStr={hojeStr}
          aulasHoje={aulasHoje}
          turmas={turmas}
          statusMap={statusMap}
          aulaAgora={aulaAgora}
          aulaJaFeita={aulaJaFeita}
          chamadaId={chamadaId}
          loadingAlunos={loadingAlunos}
          iniciandoChamada={iniciandoChamada}
          contagemPorTurma={contagemPorTurma}
          todosAlunos={todosAlunos}
          totalPerdidas={totalPerdidas}
          getAulaStatus={getAulaStatus}
          countAlunosTurma={countAlunosTurma}
          agoraOperacional={agoraOperacional}
          onRequestStart={requestStartChamada}
          onContinue={() => setFaseAtual('operacao')}
          openAulaDetail={openAulaDetail}
          setDialogPerdida={setDialogPerdida}
          setFaseAtual={setFaseAtual}
        />
      )}

      {confirmAula && (
        <ChamadaConfirmStart
          aula={confirmAula}
          turmaNome={turmas.find((t) => t.id === confirmAula.turmaId)?.nome}
          qtdAlunos={countAlunosTurma(confirmAula.turmaId)}
          busy={iniciandoChamada}
          onCancel={() => !iniciandoChamada && setConfirmAula(null)}
          onConfirm={() => {
            const aula = confirmAula;
            setConfirmAula(null);
            void iniciarChamada(aula);
          }}
        />
      )}

      <div
        style={{
          ...(isMobile
            ? {
                paddingTop: 0,
                paddingRight: faseAtual === 'operacao' || faseAtual === 'resumo' ? 12 : 0,
                paddingBottom: faseAtual !== 'briefing' ? 20 : 0,
                paddingLeft: faseAtual === 'operacao' || faseAtual === 'resumo' ? 12 : 0,
                display: faseAtual === 'briefing' && !dialogPerdida && !cancelDialog ? 'none' : 'flex',
              }
            : {
                paddingTop: 0,
                paddingBottom: 0,
                display: 'flex',
                flexDirection: 'column' as const,
                flex: '1 1 0%',
                minHeight: 0,
                height: '100%',
                overflow: 'hidden',
              }),
        }}
        className={isMobile ? '' : 'cham-desk-host'}
      >
        {/* Header (desktop only) */}
        <div className="chamada-page-header" style={{ display: isMobile || (!isMobile && faseAtual === 'briefing') ? 'none' : 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: 10, background: faseAtual === 'operacao' ? 'rgba(124,58,237,0.1)' : faseAtual === 'resumo' ? 'rgba(52,211,153,0.1)' : aulaAgora ? 'rgba(124,58,237,0.1)' : 'rgba(107,114,128,0.08)', flexShrink: 0 }}>
            {faseAtual === 'operacao' ? <ClipboardList size={16} style={{ color: '#7c3aed' }} /> : faseAtual === 'resumo' ? <Shield size={16} style={{ color: '#059669' }} /> : aulaAgora ? <ClipboardList size={16} style={{ color: '#7c3aed' }} /> : <ClipboardList size={16} style={{ color: '#9ca3af' }} />}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '1.1rem', fontWeight: 700 }}>{faseAtual === 'operacao' ? 'Chamada em Andamento' : faseAtual === 'resumo' ? 'Resumo da Chamada' : aulaAgora ? aulaAgora.numeroAula + '\u00aa Aula em Andamento' : 'Chamada'}</div>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-muted, #6b7280)' }}>{formatDateSafe(dataAtual)} {'\u2022 ' + (diaSemana ? DIA_LABELS[diaSemana] || diaSemana : '')}</div>
          </div>
          {faseAtual === 'briefing' && (
            <button onClick={() => navigate('/professor/dashboard')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: 10, border: 'none', background: 'var(--bg-card, white)', color: 'var(--text-muted, #6b7280)', cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', flexShrink: 0 }}>
              <ArrowLeft size={16} />
            </button>
          )}
        </div>

        {!isMobile && faseAtual === 'briefing' && (
          <ChamadaProfessorDesktop
            user={user}
            dataAtual={dataAtual}
            setDataAtual={setDataAtual}
            diaSemana={diaSemana}
            aulasHoje={aulasHoje}
            chamadasDia={chamadasDia}
            statusMap={statusMap}
            turmas={turmas}
            horarioAtualId={aulaAgora?.id}
            aulaAgora={aulaAgora}
            chamadaId={chamadaId}
            loadingAlunos={loadingAlunos}
            iniciandoChamada={iniciandoChamada}
            contagemPorTurma={contagemPorTurma}
            todosAlunos={todosAlunos}
            iniciarChamada={async (aula) => {
              if (aula) requestStartChamada(aula);
            }}
            openAulaDetail={openAulaDetail}
            setDialogPerdida={setDialogPerdida}
            setFaseAtual={setFaseAtual}
          />
        )}

        {/* ═══ FASE 2: OPERACAO (card + lista) ═══ */}
        {faseAtual === 'operacao' && (
          <>
            {!alunoCard ? (
              <div className="mobile-page-root animate-fade-in" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 'calc(100vh - 100px)', gap: 16, padding: '24px 16px', textAlign: 'center', background: 'var(--bg-body, #f8fafc)', boxSizing: 'border-box' }}>
                {loadingAlunos ? (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '32px 16px', background: 'var(--bg-card, white)', borderRadius: 16, border: '1px solid var(--border-color, #e5e7eb)', width: '100%', maxWidth: 360, boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
                    <div style={{ position: 'relative', width: 44, height: 44 }}>
                      <div className="ac-spinner" style={{ width: '100%', height: '100%', border: '4px solid rgba(37, 99, 235, 0.1)', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
                    </div>
                    <div style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-main, #1e293b)' }}>Carregando alunos da turma...</div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-muted, #64748b)' }}>Sincronizando dados com o servidor local</div>
                    <style>{`
                      @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                      }
                    `}</style>
                  </div>
                ) : (
                  <div className="hover-lift" style={{ background: 'var(--bg-card, white)', borderRadius: 18, padding: '28px 24px', border: '1px solid var(--border-color, #e5e7eb)', boxShadow: '0 6px 20px rgba(0,0,0,0.04)', maxWidth: 360, width: '100%', boxSizing: 'border-box' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 48, height: 48, borderRadius: 14, background: 'rgba(239, 68, 68, 0.08)', color: '#ef4444', margin: '0 auto 16px' }}>
                      <AlertTriangle size={22} />
                    </div>
                    <h3 style={{ fontSize: '1.05rem', fontWeight: 800, color: 'var(--text-main, #1e293b)', marginBottom: 8, letterSpacing: '-0.3px' }}>Turma sem Alunos</h3>
                    <p style={{ fontSize: '0.78rem', color: 'var(--text-muted, #64748b)', lineHeight: 1.5, marginBottom: 12 }}>
                      A turma <strong>{turmaAulaAgora?.nome || 'desta aula'}</strong> não tem alunos vinculados no cadastro.
                      A chamada só pode ser feita quando os alunos estiverem nesta turma (não em outra com nome parecido).
                    </p>
                    {(() => {
                      const sug = turmasComAlunosSugeridas(turmaAulaAgora, turmas, todosAlunos);
                      if (!sug.length) return null;
                      return (
                        <p style={{ fontSize: '0.72rem', color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '10px 12px', lineHeight: 1.45, marginBottom: 16, textAlign: 'left' }}>
                          <strong>Onde há alunos hoje:</strong>{' '}
                          {sug.map((s) => `${s.nome} (${s.total})`).join(' · ')}.
                          Peça à secretaria para matricular os alunos em <strong>{turmaAulaAgora?.nome}</strong>.
                        </p>
                      );
                    })()}
                    <button onClick={() => {
                      limparProgressoLocal();
                      setSelectedSlot(null);
                      setFaseAtual('briefing');
                    }} style={{ width: '100%', padding: '12px 16px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg, #1e3a5f, #2563eb)', color: 'white', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 12px rgba(37,99,235,0.2)' }}>
                      Voltar para o Painel
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
            {/* CSS para mobile layout */}
            <style>{`
              @media (max-width: 768px) {
                .chamada-page-header { display: none !important; }
                .chamada-fixed-top { display: block !important; }
                .chamada-progress-bar { display: none !important; }
                .chamada-op-parent { gap: 0 !important; }
                .chamada-op-wrap { flex-direction: column !important; flex-wrap: nowrap !important; gap: 0 !important; }
                .chamada-op-wrap .chamada-lista { max-height: none !important; height: auto !important; min-width: 0 !important; overflow-x: visible !important; overflow-y: visible !important; border-radius: 0 !important; border-left: none !important; border-right: none !important; padding: 8px 0 8px !important; margin-top: calc(118px + env(safe-area-inset-top, 0px)) !important; margin-bottom: calc(235px + env(safe-area-inset-bottom, 0px)) !important; }
                .chamada-op-wrap .chamada-lista [data-aluno-idx] { box-sizing: border-box !important; }
                .chamada-op-wrap .chamada-lista [data-aluno-idx] { scroll-margin-bottom: calc(240px + env(safe-area-inset-bottom, 0px)) !important; scroll-margin-top: calc(152px + env(safe-area-inset-top, 0px)) !important; }
                .chamada-op-wrap .chamada-card-area { position: fixed !important; bottom: 0 !important; left: 0 !important; right: 0 !important; z-index: 50 !important; min-width: 0 !important; border-top-left-radius: 16px !important; border-top-right-radius: 16px !important; border-bottom-left-radius: 0 !important; border-bottom-right-radius: 0 !important; border: 1px solid var(--border-color, #e5e7eb) !important; border-bottom: none !important; box-shadow: 0 -4px 24px rgba(0,0,0,0.12) !important; padding: 14px 16px calc(18px + env(safe-area-inset-bottom, 8px)) !important; background: var(--bg-card, white) !important; margin-bottom: 0 !important; }
              }
            `}</style>

            {/* Header fixo navy — só mobile; card com padding e cantos arredondados */}
            <div className="chamada-fixed-top" style={{
              display: 'none', position: 'fixed', top: 0, left: 0, right: 0, zIndex: 45,
              padding: 'calc(12px + env(safe-area-inset-top, 8px)) 12px 8px 12px',
              background: 'transparent',
              boxSizing: 'border-box',
            }}>
              <div style={{
                background: 'linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)',
                borderRadius: 14,
                padding: '12px 14px 10px',
                color: 'white',
                boxShadow: '0 4px 16px rgba(30, 58, 95, 0.22)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <button onClick={() => setCancelDialog(true)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 8, border: 'none', background: 'rgba(255,255,255,0.15)', color: 'white', cursor: 'pointer', flexShrink: 0 }}>
                    <ArrowLeft size={14} />
                  </button>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.85rem', fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {(selectedSlot as any)?.materia?.nome || materiaAulaAgora || ''}
                    </div>
                    <div style={{ fontSize: '0.72rem', fontWeight: 500, opacity: 0.8, marginTop: 1 }}>
                      {turmaAulaAgora?.nome || turmas.find(t => t.id === selectedSlot?.turmaId)?.nome || ''} &bull; {selectedSlot?.horaInicio || aulaAgora?.horaInicio || ''} às {selectedSlot?.horaFim || aulaAgora?.horaFim || ''}
                    </div>
                  </div>
                  <span style={{ fontSize: '0.82rem', fontWeight: 800, background: 'rgba(255,255,255,0.15)', padding: '3px 10px', borderRadius: 8 }}>{respondidos}/{totalAlunos}</span>
                </div>
                <div style={{ width: '100%', height: 3, borderRadius: 2, background: 'rgba(255,255,255,0.2)', overflow: 'hidden', marginTop: 8 }}>
                  <div style={{ height: '100%', borderRadius: 2, transition: 'width 0.3s', width: `${totalAlunos > 0 ? (respondidos / totalAlunos) * 100 : 0}%`, background: 'white' }} />
                </div>
              </div>
            </div>

            <div className="chamada-op-parent" style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
              flex: isMobile ? undefined : '1 1 0%',
              minHeight: 0,
              overflow: isMobile ? undefined : 'hidden',
            }}>
              {/* Context Header da Chamada com Foto do Professor, Matéria, Turma, Progresso e Cancelar */}
              <div className="chamada-progress-bar" style={{
                flexShrink: 0,
                background: 'var(--bg-card, white)',
                borderRadius: 14,
                padding: '12px 18px 14px',
                border: '1px solid var(--border-color, #e5e7eb)',
                boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                  {/* Info do Professor + Materia + Turma */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <UserAvatar
                      src={currentUser?.fotoUrl}
                      name={currentUser?.nome || 'Professor'}
                      role="PROFESSOR"
                      size={40}
                      shape="circle"
                      border="2px solid #059669"
                    />
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{
                          fontSize: '1.05rem',
                          fontWeight: 800,
                          color: 'var(--text-main, #0f172a)',
                          letterSpacing: '-0.2px',
                          textTransform: 'uppercase',
                        }}>
                          {materiaAulaAgora || 'AULA'}
                        </span>
                        <span style={{
                          fontSize: '0.84rem',
                          fontWeight: 800,
                          padding: '3px 10px',
                          borderRadius: 6,
                          background: 'rgba(124, 58, 237, 0.1)',
                          color: '#7c3aed',
                          letterSpacing: '0.3px',
                        }}>
                          TURMA: {turmaAulaAgora?.nome || selectedSlot?.turma?.nome || 'T'}
                        </span>
                        {(turmaAulaAgora?.turno || selectedSlot?.turma?.turno) && (
                          <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase' }}>
                            ({turmaAulaAgora?.turno || selectedSlot?.turma?.turno})
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: '0.74rem', color: '#64748b', marginTop: 1 }}>
                        {currentUser?.nome ? `Prof. ${currentUser.nome.split(' ')[0]}` : 'Professor'} &bull; Chamada em tempo real
                      </div>
                    </div>
                  </div>

                  {/* Estatistica + Botao Cancelar */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '0.92rem', fontWeight: 800, color: 'var(--text-main, #0f172a)' }}>
                        {respondidos}/{totalAlunos} <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#64748b' }}>({totalAlunos > 0 ? Math.round((respondidos / totalAlunos) * 100) : 0}%)</span>
                      </div>
                      <div style={{ fontSize: '0.68rem', color: '#9ca3af' }}>
                        {totalAlunos - respondidos === 0 ? 'Concluído' : `Faltam ${totalAlunos - respondidos}`}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setCancelDialog(true)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '6px 12px',
                        borderRadius: 8,
                        border: '1px solid #fee2e2',
                        background: '#fff5f5',
                        color: '#dc2626',
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#dc2626';
                        e.currentTarget.style.color = '#ffffff';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#fff5f5';
                        e.currentTarget.style.color = '#dc2626';
                      }}
                    >
                      <X size={14} />
                      Cancelar Chamada
                    </button>
                  </div>
                </div>

                {/* Barra de Progresso */}
                <div style={{ width: '100%', height: 7, borderRadius: 4, background: '#f1f5f9', overflow: 'hidden' }}>
                  <div style={{
                    height: '100%',
                    borderRadius: 4,
                    transition: 'width 0.3s ease',
                    width: `${totalAlunos > 0 ? (respondidos / totalAlunos) * 100 : 0}%`,
                    background: 'linear-gradient(90deg, #6d28d9, #8b5cf6, #10b981)',
                  }} />
                </div>
              </div>

              <style>{`
                .btn-chamada-presente:hover:not(:disabled) {
                  background: #dcfce7 !important;
                  color: #166534 !important;
                  border-color: #86efac !important;
                }
                .btn-chamada-presente.is-selected {
                  background: #16a34a !important;
                  color: #ffffff !important;
                  border-color: #16a34a !important;
                  box-shadow: 0 4px 14px rgba(22,163,74,0.35) !important;
                }
                .btn-chamada-ausente:hover:not(:disabled) {
                  background: #fee2e2 !important;
                  color: #991b1b !important;
                  border-color: #fca5a5 !important;
                }
                .btn-chamada-ausente.is-selected {
                  background: #dc2626 !important;
                  color: #ffffff !important;
                  border-color: #dc2626 !important;
                  box-shadow: 0 4px 14px rgba(220,38,38,0.35) !important;
                }
                .btn-chamada-atrasado:hover:not(:disabled) {
                  background: #fef3c7 !important;
                  color: #92400e !important;
                  border-color: #fcd34d !important;
                }
                .btn-chamada-atrasado.is-selected {
                  background: #d97706 !important;
                  color: #ffffff !important;
                  border-color: #d97706 !important;
                  box-shadow: 0 2px 8px rgba(217,119,6,0.35) !important;
                }
                .btn-chamada-justificado:hover:not(:disabled) {
                  background: #e0e7ff !important;
                  color: #3730a3 !important;
                  border-color: #a5b4fc !important;
                }
                .btn-chamada-justificado.is-selected {
                  background: #4f46e5 !important;
                  color: #ffffff !important;
                  border-color: #4f46e5 !important;
                  box-shadow: 0 2px 8px rgba(79,70,229,0.35) !important;
                }
                .btn-chamada-dispensado:hover:not(:disabled) {
                  background: #e0f2fe !important;
                  color: #0369a1 !important;
                  border-color: #7dd3fc !important;
                }
                .btn-chamada-dispensado.is-selected {
                  background: #0891b2 !important;
                  color: #ffffff !important;
                  border-color: #0891b2 !important;
                  box-shadow: 0 2px 8px rgba(8,145,178,0.35) !important;
                }
              `}</style>

              {/* Layout: Lista + Card */}
              <div className="chamada-op-wrap" style={{
                display: 'flex',
                gap: 20,
                alignItems: 'flex-start',
                flexDirection: isMobile ? 'column' : 'row',
                flexWrap: isMobile ? 'wrap' : 'nowrap',
                flex: isMobile ? undefined : '1 1 0%',
                minHeight: 0,
                overflow: isMobile ? undefined : 'hidden',
              }}>
                {/* Lista de alunos: no Desktop exibe os pendentes; no Mobile exibe todos */}
                <div className="chamada-lista" style={{
                  flex: isMobile ? '1 1 220px' : '0 0 320px',
                  width: isMobile ? '100%' : 320,
                  maxHeight: isMobile ? 'calc(100vh - 310px)' : 'calc(100vh - 240px)',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  scrollbarWidth: 'thin',
                  minWidth: isMobile ? 200 : 280,
                  background: isMobile ? 'transparent' : 'var(--bg-card, white)',
                  borderRadius: isMobile ? 0 : 14,
                  border: isMobile ? 'none' : '1px solid var(--border-color, #e5e7eb)',
                  padding: 0,
                  boxSizing: 'border-box',
                }}>
                  <div style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                    background: 'var(--bg-card, white)',
                    padding: isMobile ? '10px 0 8px' : '10px 12px 8px',
                    fontSize: '0.68rem',
                    fontWeight: 700,
                    color: '#9ca3af',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: '1px solid var(--border-color, #f1f5f9)',
                    borderTopLeftRadius: isMobile ? 0 : 14,
                    borderTopRightRadius: isMobile ? 0 : 14,
                  }}>
                    {isMobile ? `Lista de alunos · ${totalAlunos}` : `A chamar · ${alunosPendentes.length}`}
                  </div>
                  <div style={{ padding: isMobile ? '0' : '6px 6px 8px' }}>
                    {!isMobile && alunosPendentes.length === 0 ? (
                      <div style={{ padding: '32px 16px', textAlign: 'center', color: '#6b7280', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                        <CheckCircle2 size={32} style={{ color: '#10b981', marginBottom: 8 }} />
                        <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#1e293b' }}>Chamada concluída!</div>
                        <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: 2 }}>Todos os {totalAlunos} alunos foram chamados.</div>
                      </div>
                    ) : (
                      (isMobile ? alunosTurmaOrdenados : alunosPendentes).map((a, i) => {
                        const st = presencas[a.id];
                        const originalIdx = isMobile ? i : alunosTurmaOrdenados.findIndex(x => x.id === a.id);
                        const isAtual = originalIdx === alunoIdx;
                        const rfid = rfidMap[a.id];
                        const statusColor = st === 'PRESENTE' ? '#059669' : st === 'AUSENTE' ? '#dc2626' : st === 'ATRASADO' ? '#d97706' : st ? '#6366f1' : '#d1d5db';
                        return (
                          <div key={a.id} data-aluno-idx={originalIdx} onClick={() => setAlunoIdx(originalIdx)} style={{
                            display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 10,
                            padding: isMobile ? '10px 12px' : '8px 10px', cursor: 'pointer',
                            boxSizing: 'border-box',
                            background: isAtual ? 'rgba(37,99,235,0.08)' : 'var(--bg-card, white)',
                            borderRadius: isMobile ? 12 : 10,
                            margin: isMobile ? '0 0 6px 0' : '0 0 3px 0',
                            border: isMobile
                              ? (isAtual ? '1px solid #2563eb' : '1px solid var(--border-color, #e5e7eb)')
                              : 'none',
                            borderLeft: !isMobile ? (isAtual ? '3px solid #2563eb' : '3px solid transparent') : undefined,
                            boxShadow: isMobile && isAtual
                              ? 'inset 0 0 0 1px #2563eb, 0 2px 8px rgba(37,99,235,0.12)'
                              : isAtual ? '0 2px 8px rgba(37,99,235,0.12)' : 'none',
                            transition: 'all 0.12s',
                          }}>
                            {/* Avatar */}
                            <UserAvatar
                              src={a.fotoUrl}
                              name={a.nome}
                              role="ALUNO"
                              genero={a.genero}
                              size={isMobile ? 28 : 26}
                              border={st ? `2px solid ${statusColor}` : '2px solid #e5e7eb'}
                            />
                            {/* Nome inteligente */}
                            <span style={{
                              fontSize: isMobile ? '0.85rem' : '0.8rem', fontWeight: isAtual ? 700 : 500, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                              color: isAtual ? '#1e293b' : st ? '#374151' : '#6b7280',
                            }}>
                              {displayNames[a.id] || a.nome}
                            </span>
                            {rfid && (
                              <span style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }} title={rfid.entrou ? `Entrou ${rfid.horaEntrada || ''}` : 'Sem entrada RFID'}>
                                {rfid.entrou ? <Shield size={11} style={{ color: '#16a34a' }} /> : <ShieldAlert size={11} style={{ color: '#dc2626' }} />}
                              </span>
                            )}
                            {/* Thumb toggle buttons no Mobile */}
                            {isMobile ? (
                              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                                <button onClick={(e) => {
                                  e.stopPropagation();
                                  if (st === 'PRESENTE') { setPresencas((prev: any) => { const n = {...prev}; delete n[a.id]; return n; }); }
                                  else { marcarEAvancar(a.id, 'PRESENTE'); }
                                }} style={{
                                  width: 40, height: 40, borderRadius: 10, border: 'none', cursor: 'pointer',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  background: st === 'PRESENTE' ? '#059669' : 'rgba(0,0,0,0.05)',
                                  color: st === 'PRESENTE' ? 'white' : '#9ca3af',
                                  transition: 'all 0.15s',
                                  boxShadow: st === 'PRESENTE' ? '0 2px 8px rgba(5,150,105,0.4)' : 'none',
                                }}>
                                  <ThumbsUp size={16} />
                                </button>
                                <button onClick={(e) => {
                                  e.stopPropagation();
                                  if (st === 'AUSENTE') { setPresencas((prev: any) => { const n = {...prev}; delete n[a.id]; return n; }); }
                                  else { marcarEAvancar(a.id, 'AUSENTE'); }
                                }} style={{
                                  width: 40, height: 40, borderRadius: 10, border: 'none', cursor: 'pointer',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  background: st === 'AUSENTE' ? '#dc2626' : 'rgba(0,0,0,0.05)',
                                  color: st === 'AUSENTE' ? 'white' : '#9ca3af',
                                  transition: 'all 0.15s',
                                  boxShadow: st === 'AUSENTE' ? '0 2px 8px rgba(220,38,38,0.4)' : 'none',
                                }}>
                                  <ThumbsDown size={16} />
                                </button>
                              </div>
                            ) : (
                              st && (
                                <span style={{ fontSize: '0.58rem', fontWeight: 700, padding: '2px 6px', borderRadius: 4, flexShrink: 0, background: STATUS_LABELS[st]?.bg || '#f3f4f6', color: STATUS_LABELS[st]?.color || '#6b7280', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                                  {st}
                                </span>
                              )
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* Card do aluno (fixo/sticky no desktop, fixo embaixo no mobile) */}
                <div className="chamada-card-area" style={{
                  flex: '1 1 auto',
                  minWidth: isMobile ? undefined : 360,
                  position: isMobile ? undefined : 'sticky',
                  top: isMobile ? undefined : 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 14,
                  background: 'var(--bg-card, white)',
                  borderRadius: 16,
                  padding: isMobile ? '16px 16px 14px' : '22px 24px 18px',
                  border: '1px solid var(--border-color, #e5e7eb)',
                  boxShadow: isMobile ? undefined : '0 4px 20px rgba(0,0,0,0.05)',
                }}>
                  {/* Aluno info com foto grande na esquerda e nome destacado */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 14 : 18 }}>
                    <UserAvatar
                      src={alunoCard.fotoUrl}
                      name={alunoCard.nome}
                      role="ALUNO"
                      genero={alunoCard.genero}
                      size={isMobile ? 52 : 72}
                      shape="circle"
                      border="3px solid #7c3aed"
                      style={{ flexShrink: 0 }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: isMobile ? '1.1rem' : '1.45rem',
                        fontWeight: 800,
                        color: 'var(--text-main, #0f172a)',
                        letterSpacing: '-0.3px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        lineHeight: 1.2,
                      }}>
                        {displayNames[alunoCard.id] || alunoCard.nome}
                      </div>
                      <div style={{ fontSize: '0.72rem', color: '#64748b', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                        <span style={{ fontWeight: 600, color: '#8b5cf6' }}>{alunoIdx + 1} de {totalAlunos}</span>
                        {alunoCard.matricula && (
                          <span style={{ color: '#94a3b8' }}>&bull; {alunoCard.matricula}</span>
                        )}
                        {rfidMap[alunoCard.id] && (
                          <span style={{
                            fontSize: '0.62rem', fontWeight: 700, padding: '1px 6px', borderRadius: 4,
                            background: rfidMap[alunoCard.id].entrou ? '#dcfce7' : '#fee2e2',
                            color: rfidMap[alunoCard.id].entrou ? '#166534' : '#991b1b',
                          }}>
                            {rfidMap[alunoCard.id].entrou ? `✔ Entrou ${rfidMap[alunoCard.id].horaEntrada || ''}` : '✖ Sem entrada'}
                          </span>
                        )}
                        {presencas[alunoCard.id] && (
                          <span style={{
                            padding: '2px 8px', borderRadius: 6, fontSize: '0.68rem', fontWeight: 700,
                            background: STATUS_LABELS[presencas[alunoCard.id]]?.bg
                              || (presencas[alunoCard.id] === 'PRESENTE' ? '#dcfce7' : presencas[alunoCard.id] === 'AUSENTE' ? '#fee2e2' : '#fef3c7'),
                            color: STATUS_LABELS[presencas[alunoCard.id]]?.color
                              || (presencas[alunoCard.id] === 'PRESENTE' ? '#166534' : presencas[alunoCard.id] === 'AUSENTE' ? '#991b1b' : '#92400e'),
                          }}>
                            {presencas[alunoCard.id] === 'JUSTIFICADO'
                              ? 'Justificado'
                              : presencas[alunoCard.id] === 'PRESENTE' ? '✔ Presente' : presencas[alunoCard.id] === 'AUSENTE' ? '✖ Ausente' : STATUS_LABELS[presencas[alunoCard.id]]?.label || presencas[alunoCard.id]}
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Icones de voz */}
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                      <button
                        onClick={handleToggleTts}
                        title={ttsActive ? 'Desativar leitura de nomes' : 'Ativar leitura de nomes'}
                        style={{
                          width: 32, height: 32, borderRadius: '50%', border: 'none', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          background: ttsActive ? '#2563eb' : '#f1f5f9',
                          color: ttsActive ? 'white' : '#94a3b8',
                          transition: 'all 0.2s',
                        }}
                      >
                        {ttsActive ? <Volume2 size={17} /> : <VolumeX size={17} />}
                      </button>
                      <button
                        onClick={handleToggleMic}
                        title={micActive ? 'Desativar microfone' : 'Ativar comando de voz'}
                        style={{
                          width: 32, height: 32, borderRadius: '50%', border: 'none', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          background: micActive ? '#dc2626' : '#f1f5f9',
                          color: micActive ? 'white' : '#94a3b8',
                          transition: 'all 0.2s',
                          animation: micActive ? 'micPulse 1.5s ease-in-out infinite' : 'none',
                          boxShadow: micActive ? '0 0 0 0 rgba(220,38,38,0.4)' : 'none',
                        }}
                      >
                        {micActive ? <Mic size={17} /> : <MicOff size={17} />}
                      </button>
                    </div>
                  </div>

                  {presencas[alunoCard.id] === 'JUSTIFICADO' && (
                    <div style={{
                      padding: '8px 10px', borderRadius: 8, background: '#eef2ff', border: '1px solid #c7d2fe',
                      fontSize: '0.72rem', color: '#3730a3', fontWeight: 600,
                    }}>
                      Falta abonada pela escola — marcado como Justificado (não alterar).
                    </div>
                  )}

                  {/* Botoes PRESENTE / AUSENTE com estado neutro quando nao marcados */}
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button
                      type="button"
                      disabled={presencas[alunoCard.id] === 'JUSTIFICADO'}
                      onClick={() => marcarEAvancar(alunoCard.id, 'PRESENTE')}
                      className={`btn-chamada-presente ${presencas[alunoCard.id] === 'PRESENTE' ? 'is-selected' : ''}`}
                      style={{
                        flex: 1, height: 44, borderRadius: 12, fontSize: '0.88rem', fontWeight: 500,
                        cursor: presencas[alunoCard.id] === 'JUSTIFICADO' ? 'not-allowed' : 'pointer', transition: 'all 0.15s',
                        opacity: presencas[alunoCard.id] === 'JUSTIFICADO' ? 0.45 : 1,
                        background: presencas[alunoCard.id] === 'PRESENTE' ? '#16a34a' : 'var(--bg-card, #f8fafc)',
                        color: presencas[alunoCard.id] === 'PRESENTE' ? 'white' : 'var(--text-main, #334155)',
                        border: presencas[alunoCard.id] === 'PRESENTE' ? '1px solid #16a34a' : '1px solid var(--border-color, #e2e8f0)',
                      }}
                    >
                      Presente
                    </button>
                    <button
                      type="button"
                      disabled={presencas[alunoCard.id] === 'JUSTIFICADO'}
                      onClick={() => marcarEAvancar(alunoCard.id, 'AUSENTE')}
                      className={`btn-chamada-ausente ${presencas[alunoCard.id] === 'AUSENTE' ? 'is-selected' : ''}`}
                      style={{
                        flex: 1, height: 44, borderRadius: 12, fontSize: '0.88rem', fontWeight: 500,
                        cursor: presencas[alunoCard.id] === 'JUSTIFICADO' ? 'not-allowed' : 'pointer', transition: 'all 0.15s',
                        opacity: presencas[alunoCard.id] === 'JUSTIFICADO' ? 0.45 : 1,
                        background: presencas[alunoCard.id] === 'AUSENTE' ? '#dc2626' : 'var(--bg-card, #f8fafc)',
                        color: presencas[alunoCard.id] === 'AUSENTE' ? 'white' : 'var(--text-main, #334155)',
                        border: presencas[alunoCard.id] === 'AUSENTE' ? '1px solid #dc2626' : '1px solid var(--border-color, #e2e8f0)',
                      }}
                    >
                      Ausente
                    </button>
                  </div>

                  {/* Opcoes secundarias com nomes completos no desktop + finalizar */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {(['ATRASADO', 'JUSTIFICADO', 'DISPENSADO'] as StatusPresencaAula[]).map(s => {
                        const isSelected = presencas[alunoCard.id] === s;
                        const label = s === 'ATRASADO' ? 'Atrasado' : s === 'JUSTIFICADO' ? (isMobile ? 'Justif.' : 'Justificado') : (isMobile ? 'Disp.' : 'Dispensado');
                        const btnClass = s === 'ATRASADO' ? 'btn-chamada-atrasado' : s === 'JUSTIFICADO' ? 'btn-chamada-justificado' : 'btn-chamada-dispensado';
                        return (
                          <button
                            type="button"
                            key={s}
                            disabled={presencas[alunoCard.id] === 'JUSTIFICADO' && s !== 'JUSTIFICADO'}
                            onClick={() => marcarEAvancar(alunoCard.id, s)}
                            className={`${btnClass} ${isSelected ? 'is-selected' : ''}`}
                            style={{
                              padding: '6px 12px', borderRadius: 8,
                              fontSize: '0.72rem', fontWeight: 500,
                              cursor: presencas[alunoCard.id] === 'JUSTIFICADO' && s !== 'JUSTIFICADO' ? 'not-allowed' : 'pointer',
                              opacity: presencas[alunoCard.id] === 'JUSTIFICADO' && s !== 'JUSTIFICADO' ? 0.45 : 1,
                              transition: 'all 0.15s',
                              background: isSelected ? (s === 'ATRASADO' ? '#d97706' : s === 'JUSTIFICADO' ? '#4f46e5' : '#0891b2') : 'transparent',
                              color: isSelected ? 'white' : '#64748b',
                              border: isSelected ? '1px solid transparent' : '1px solid var(--border-color, #e2e8f0)',
                            }}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      {todosRespondidos ? (
                        <button onClick={() => setFaseAtual('resumo')} style={{
                          padding: '6px 16px', borderRadius: 8, border: 'none',
                          background: 'linear-gradient(135deg, #6d28d9, #8b5cf6)', color: 'white',
                          fontWeight: 500, fontSize: '0.75rem', cursor: 'pointer',
                        }}>
                          FINALIZAR &rarr;
                        </button>
                      ) : (
                        <span style={{ fontSize: '0.68rem', color: '#9ca3af' }}>Faltam {totalAlunos - respondidos}</span>
                      )}
                    </div>
                  </div>
                  {/* Feedback de voz */}
                  {micActive && lastHeard && (
                    <div style={{ fontSize: '0.65rem', color: '#dc2626', textAlign: 'center', padding: '4px 0', opacity: 0.7, textTransform: 'uppercase' }}>
                      🎤 &ldquo;{lastHeard}&rdquo;
                    </div>
                  )}
                </div>

                {/* Lista de alunos chamados (Desktop only — visivel apos o primeiro chamado) */}
                {!isMobile && alunosChamados.length > 0 && (
                  <div className="chamada-chamados-lista" style={{
                    flex: '0 0 320px',
                    width: 320,
                    maxHeight: 'calc(100vh - 240px)',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    scrollbarWidth: 'thin',
                    minWidth: 280,
                    background: 'var(--bg-card, white)',
                    borderRadius: 14,
                    border: '1px solid var(--border-color, #e5e7eb)',
                    padding: 0,
                    boxSizing: 'border-box',
                  }}>
                    <div style={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 10,
                      background: 'var(--bg-card, white)',
                      padding: '10px 12px 8px',
                      fontSize: '0.68rem',
                      fontWeight: 700,
                      color: '#059669',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      borderBottom: '1px solid var(--border-color, #f1f5f9)',
                      borderTopLeftRadius: 14,
                      borderTopRightRadius: 14,
                    }}>
                      <span>Chamados · {alunosChamados.length}</span>
                      <span style={{ fontSize: '0.62rem', fontWeight: 600, color: '#9ca3af' }}>{Math.round((alunosChamados.length / totalAlunos) * 100)}%</span>
                    </div>
                    <div style={{ padding: '6px 6px 8px' }}>
                      {alunosChamados.map((a: any) => {
                        const st = presencas[a.id];
                        const originalIdx = alunosTurmaOrdenados.findIndex((x: any) => x.id === a.id);
                        const isAtual = originalIdx === alunoIdx;
                        const statusColor = st === 'PRESENTE' ? '#059669' : st === 'AUSENTE' ? '#dc2626' : st === 'ATRASADO' ? '#d97706' : st ? '#6366f1' : '#d1d5db';
                        return (
                          <div key={a.id} data-aluno-idx={originalIdx} onClick={() => setAlunoIdx(originalIdx)} style={{
                            display: 'flex', alignItems: 'center', gap: 10,
                            padding: '7px 10px', cursor: 'pointer',
                            boxSizing: 'border-box',
                            background: isAtual ? 'rgba(37,99,235,0.08)' : 'var(--bg-card, white)',
                            borderRadius: 10,
                            margin: '0 0 3px 0',
                            borderLeft: isAtual ? '3px solid #2563eb' : '3px solid transparent',
                            boxShadow: isAtual ? '0 2px 8px rgba(37,99,235,0.12)' : 'none',
                            transition: 'all 0.12s',
                          }}>
                            <UserAvatar
                              src={a.fotoUrl}
                              name={a.nome}
                              role="ALUNO"
                              genero={a.genero}
                              size={26}
                              border={`2px solid ${statusColor}`}
                            />
                            <span style={{
                              fontSize: '0.8rem', fontWeight: isAtual ? 700 : 500, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                              color: isAtual ? '#1e293b' : '#374151',
                            }}>
                              {displayNames[a.id] || a.nome}
                            </span>
                            <span style={{
                              fontSize: '0.58rem', fontWeight: 700, padding: '2px 6px', borderRadius: 4, flexShrink: 0,
                              background: STATUS_LABELS[st]?.bg || '#f3f4f6',
                              color: STATUS_LABELS[st]?.color || '#6b7280',
                              textTransform: 'uppercase', letterSpacing: '0.3px',
                            }}>
                              {st}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
              </>
            )}
          </>
        )}

        {/* ═══ FASE 3: RESUMO ═══ */}
        {faseAtual === 'resumo' && (
          <>
            <style>{`
              @media (max-width: 768px) {
                .chamada-page-header { display: none !important; }
                .chamada-resumo-header-mobile { display: block !important; }
                .chamada-resumo-footer { position: fixed !important; bottom: 0 !important; left: 0 !important; right: 0 !important; z-index: 50 !important; border-top-left-radius: 16px !important; border-top-right-radius: 16px !important; border-bottom-left-radius: 0 !important; border-bottom-right-radius: 0 !important; border: 1px solid var(--border-color, #e5e7eb) !important; border-bottom: none !important; box-shadow: 0 -4px 24px rgba(0,0,0,0.12) !important; padding: 12px 16px calc(14px + env(safe-area-inset-bottom, 8px)) !important; background: var(--bg-card, white) !important; margin-bottom: 0 !important; }
                .chamada-resumo-lista { margin-bottom: calc(90px + env(safe-area-inset-bottom, 0px)) !important; margin-left: -16px !important; margin-right: -16px !important; padding-left: 16px !important; padding-right: 16px !important; }
                .chamada-resumo-desktop { flex-direction: column !important; }
                .chamada-resumo-desktop > div { width: 100% !important; }
                .chamada-resumo-sidebar { display: none !important; }
              }
              @media (min-width: 769px) {
                .chamada-resumo-footer { display: none !important; }
                .chamada-resumo-header-mobile { display: none !important; }
              }
            `}</style>

            {/* Navy header mobile */}
            <div className="chamada-resumo-header-mobile" style={{ display: 'none', position: 'fixed', top: 0, left: 0, right: 0, zIndex: 45, padding: '0 0 8px', background: 'var(--bg-body, #f8fafc)' }}>
              <div style={{
                background: 'linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)',
                borderRadius: '0 0 16px 16px', padding: '12px 16px 10px',
                paddingTop: 'calc(12px + env(safe-area-inset-top, 8px))',
                color: 'white', boxShadow: '0 2px 12px rgba(30, 58, 95, 0.25)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <button onClick={() => { setAlunoIdx(0); setFaseAtual('operacao'); }} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 8, border: 'none', background: 'rgba(255,255,255,0.15)', color: 'white', cursor: 'pointer', flexShrink: 0 }}>
                    <ArrowLeft size={14} />
                  </button>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.85rem', fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {(selectedSlot as any)?.materia?.nome || materiaAulaAgora || ''}
                    </div>
                    <div style={{ fontSize: '0.72rem', fontWeight: 500, opacity: 0.8, marginTop: 1 }}>
                      {turmaAulaAgora?.nome || turmas.find(t => t.id === selectedSlot?.turmaId)?.nome || ''} &bull; {selectedSlot?.horaInicio || aulaAgora?.horaInicio || ''} às {selectedSlot?.horaFim || aulaAgora?.horaFim || ''}
                    </div>
                  </div>
                </div>
                {/* KPI chips inline */}
                <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                  {[
                    { label: 'Presentes', value: presentes, bg: 'rgba(22,163,74,0.2)', color: '#bbf7d0' },
                    { label: 'Faltas', value: faltas, bg: 'rgba(220,38,38,0.2)', color: '#fecaca' },
                    { label: 'Atrasados', value: atrasados, bg: 'rgba(217,119,6,0.2)', color: '#fde68a' },
                    { label: 'Outros', value: totalAlunos - presentes - faltas - atrasados, bg: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)' },
                  ].map(k => (
                    <div key={k.label} style={{ flex: 1, textAlign: 'center', padding: '4px 2px', borderRadius: 8, background: k.bg, opacity: k.value === 0 ? 0.4 : 1 }}>
                      <div style={{ fontSize: '0.82rem', fontWeight: 800, color: k.color }}>{k.value}</div>
                      <div style={{ fontSize: '0.5rem', color: k.color, fontWeight: 500, textTransform: 'uppercase' }}>{k.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="chamada-resumo-desktop" style={{ display: 'flex', gap: 18, alignItems: 'flex-start' }}>
              {/* Lista de alunos */}
              <div style={{ flex: '1 1 0%', minWidth: 0 }}>
                <div className="chamada-resumo-lista" style={{
                  background: isMobile ? 'transparent' : 'var(--bg-card, white)',
                  borderRadius: isMobile ? 0 : 12,
                  border: isMobile ? 'none' : '1px solid var(--border-color, #e5e7eb)',
                  padding: isMobile ? 'calc(140px + env(safe-area-inset-top, 0px)) 0 8px' : '8px 4px',
                  overflow: 'hidden',
                }}>
                  {/* Grouped student lists */}
                  {(() => {
                    const groups = [
                      { key: 'PRESENTE', title: 'Alunos presentes', color: '#059669', bg: '#dcfce7', borderColor: '#bbf7d0' },
                      { key: 'AUSENTE', title: 'Alunos ausentes', color: '#dc2626', bg: '#fee2e2', borderColor: '#fecaca' },
                      { key: 'ATRASADO', title: 'Alunos atrasados', color: '#d97706', bg: '#fef3c7', borderColor: '#fde68a' },
                      { key: 'JUSTIFICADO', title: 'Alunos justificados', color: '#6366f1', bg: '#ede9fe', borderColor: '#c4b5fd' },
                      { key: 'DISPENSADO', title: 'Alunos dispensados', color: '#6b7280', bg: '#f3f4f6', borderColor: '#e5e7eb' },
                    ];
                    return groups.map(g => {
                      const items = alunosTurma.filter((a: any) => presencas[a.id] === g.key);
                      if (items.length === 0) return null;
                      return (
                        <div key={g.key} style={{ marginBottom: 12 }}>
                          {/* Section title */}
                          <div style={{ padding: isMobile ? '10px 10px 6px' : '8px 10px 6px', fontSize: '0.7rem', fontWeight: 700, color: g.color, display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ background: g.bg, color: g.color, padding: '2px 8px', borderRadius: 6, fontSize: '0.62rem', fontWeight: 800 }}>{items.length}</span>
                            {g.title}
                          </div>
                          {/* Student rows with separators */}
                          <div style={{ background: isMobile ? 'var(--bg-card, white)' : 'transparent', borderRadius: 10, border: isMobile ? '1px solid var(--border-color, #e5e7eb)' : 'none', overflow: 'hidden' }}>
                            {items.map((a: any, itemIdx: number) => {
                              const idx = alunosTurma.indexOf(a);
                              const initials = getIniciais(a.nome);
                              const isLast = itemIdx === items.length - 1;
                              return (
                                <div key={a.id} onClick={() => { setAlunoIdx(idx); setFaseAtual('operacao'); }} style={{
                                  display: 'flex', alignItems: 'center', gap: 10, padding: isMobile ? '10px 12px' : '7px 10px', cursor: 'pointer',
                                  transition: 'all 0.12s',
                                  borderBottom: !isLast ? '1px solid var(--border-color, #f1f5f9)' : 'none',
                                }}
                                  onMouseEnter={(e: any) => { e.currentTarget.style.background = '#f8fafc'; }}
                                  onMouseLeave={(e: any) => { e.currentTarget.style.background = 'transparent'; }}>
                                  <div style={{
                                    width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    background: g.bg, border: `1.5px solid ${g.borderColor}`,
                                    fontSize: '0.52rem', fontWeight: 700, color: g.color, flexShrink: 0,
                                  }}>
                                    {initials}
                                  </div>
                                  <span style={{ fontSize: '0.75rem', fontWeight: 500, flex: 1, color: '#374151' }}>
                                    {a.nome}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>

                {/* Footer flutuante MOBILE */}
                <div className="chamada-resumo-footer" style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                  <button onClick={() => { setAlunoIdx(0); setFaseAtual('operacao'); }} style={{
                    flex: 1, padding: '10px 0', borderRadius: 10, border: '1px solid var(--border-color, #e2e8f0)',
                    background: 'var(--bg-card, white)', fontWeight: 600, fontSize: '0.78rem', cursor: 'pointer', color: 'var(--text-main, #1e293b)',
                  }}>Revisar</button>
                  <button onClick={handleSalvar} disabled={saving || !todosRespondidos} style={{
                    flex: 2, padding: '10px 0', borderRadius: 10, border: 'none',
                    background: todosRespondidos ? 'linear-gradient(135deg, #059669, #10b981)' : '#d1d5db',
                    color: 'white', fontWeight: 700, fontSize: '0.78rem',
                    cursor: todosRespondidos ? 'pointer' : 'not-allowed',
                    boxShadow: todosRespondidos ? '0 4px 16px rgba(5,150,105,0.3)' : 'none',
                  }}>
                    {saving ? 'Salvando...' : todosRespondidos ? 'Salvar chamada' : `Faltam ${totalAlunos - respondidos} alunos`}
                  </button>
                </div>
              </div>

              {/* Painel desktop */}
              <div className="chamada-resumo-sidebar" style={{ position: 'sticky', top: 20, width: 320, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div style={{ background: 'var(--bg-card, white)', borderRadius: 14, padding: '20px 22px', border: '1px solid var(--border-color, #e5e7eb)' }}>
                  <h3 style={{ fontSize: '0.88rem', fontWeight: 700, margin: '0 0 6px', color: 'var(--text-main, #1e293b)' }}>
                    Finalizar chamada
                  </h3>
                  <p style={{ fontSize: '0.72rem', color: '#9ca3af', margin: '0 0 14px' }}>
                    {materiaAulaAgora} &bull; {turmaAulaAgora?.nome}
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                    {[
                      { label: 'Presentes', value: presentes, color: '#16a34a', bg: '#dcfce7' },
                      { label: 'Faltas', value: faltas, color: '#dc2626', bg: '#fee2e2' },
                      { label: 'Atrasados', value: atrasados, color: '#d97706', bg: '#fef3c7' },
                      { label: 'Outros', value: totalAlunos - presentes - faltas - atrasados, color: '#6b7280', bg: '#f3f4f6' },
                    ].map(k => (
                      <div key={k.label} style={{ textAlign: 'center', padding: '10px 4px', borderRadius: 10, background: k.bg, opacity: k.value === 0 ? 0.4 : 1, transition: 'opacity 0.2s' }}>
                        <div style={{ fontSize: '1.2rem', fontWeight: 800, color: k.color }}>{k.value}</div>
                        <div style={{ fontSize: '0.6rem', color: k.color, fontWeight: 600 }}>{k.label}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {!todosRespondidos && (
                  <div style={{ background: '#fef3c7', borderRadius: 10, padding: '12px 16px', border: '1px solid #fde68a', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <AlertTriangle size={16} color="#d97706" />
                    <span style={{ fontSize: '0.78rem', color: '#92400e', fontWeight: 600 }}>
                      {totalAlunos - respondidos} aluno{totalAlunos - respondidos !== 1 ? 's' : ''} sem resposta
                    </span>
                  </div>
                )}

                <div className="chamada-resumo-actions" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <button onClick={handleSalvar} disabled={saving || !todosRespondidos} style={{
                    width: '100%', padding: '14px 0', borderRadius: 12, border: 'none',
                    background: todosRespondidos ? 'linear-gradient(135deg, #059669, #10b981)' : '#d1d5db',
                    color: 'white', fontWeight: 700, fontSize: '0.88rem',
                    cursor: todosRespondidos ? 'pointer' : 'not-allowed',
                    boxShadow: todosRespondidos ? '0 6px 24px rgba(5,150,105,0.3)' : 'none',
                    transition: 'all 0.2s',
                  }}>
                    {saving ? 'Salvando...' : todosRespondidos ? 'Salvar chamada' : `Faltam ${totalAlunos - respondidos} alunos`}
                  </button>
                  <button onClick={() => { setAlunoIdx(0); setFaseAtual('operacao'); }} style={{
                    width: '100%', padding: '10px 0', borderRadius: 10, border: '1px solid var(--border-color, #e2e8f0)',
                    background: 'var(--bg-card, white)', fontWeight: 600, fontSize: '0.78rem', cursor: 'pointer',
                    color: 'var(--text-main, #6b7280)', transition: 'all 0.2s',
                  }}>
                    Voltar e revisar
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        {/* â”€â”€ Dialog: Cancelar chamada â”€â”€ */}
        {cancelDialog && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <div style={{ background: 'var(--bg-card, white)', borderRadius: 16, padding: '28px 24px', maxWidth: 380, width: '100%', textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 800, margin: '0 0 8px', color: '#1e293b' }}>Cancelar chamada?</h3>
              <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: '0 0 20px' }}>Seu progresso ({respondidos}/{totalAlunos}) ser&aacute; salvo localmente.</p>
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => setCancelDialog(false)} style={{ flex: 1, padding: '12px 0', borderRadius: 10, border: '1px solid #e2e8f0', background: 'var(--bg-card, white)', fontWeight: 600, cursor: 'pointer', color: '#1e293b' }}>Continuar</button>
                <button onClick={cancelarChamada} style={{ flex: 1, padding: '12px 0', borderRadius: 10, border: 'none', background: '#dc2626', color: 'white', fontWeight: 700, cursor: 'pointer' }}>Sair e Salvar</button>
              </div>
            </div>
          </div>
        )}

        {/* ── Dialog: Chamada fora do horario (aviso, NAO bloqueia) ── */}
        {dialogPerdida && (() => {
          return (
          <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={() => setDialogPerdida(null)}>
            <div onClick={e => e.stopPropagation()} style={{ background: 'var(--bg-card, white)', borderRadius: 16, padding: '28px 24px', maxWidth: 400, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <AlertTriangle size={20} color="#d97706" />
                </div>
                <div>
                  <div style={{ fontSize: '0.92rem', fontWeight: 700, color: '#1e293b' }}>
                    Chamada Fora do Horário
                  </div>
                  <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: 1 }}>
                    Será enviada para auditoria da Coordenação
                  </div>
                </div>
              </div>
              <div style={{ background: '#f8fafc', borderRadius: 10, padding: '10px 14px', marginBottom: 12, border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#334155' }}>{dialogPerdida!.materia?.nome || `${dialogPerdida!.numeroAula}\u00aa Aula`}</div>
                <div style={{ fontSize: '0.72rem', color: '#6b7280', marginTop: 2 }}>{turmas.find(t => t.id === dialogPerdida!.turmaId)?.nome || ''} • {dialogPerdida!.horaInicio}–{dialogPerdida!.horaFim}</div>
              </div>
              <p style={{ fontSize: '0.78rem', color: '#92400e', margin: '0 0 14px', lineHeight: 1.45, background: '#fffbeb', padding: '10px 12px', borderRadius: 8, border: '1px solid #fef3c7' }}>
                O horário previsto para esta aula já expirou. Você pode preencher a presença normalmente, e a chamada será marcada para <strong>homologação da Coordenação Pedagógica</strong>.
              </p>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setDialogPerdida(null)} style={{ flex: 1, padding: '11px 0', borderRadius: 10, border: '1px solid #e2e8f0', background: 'white', color: '#64748b', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer' }}>Cancelar</button>
                <button onClick={() => { const aula = dialogPerdida!; setDialogPerdida(null); iniciarChamada(aula, { foraDoHorario: true }); }} style={{ flex: 1, padding: '11px 0', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg, #4f46e5, #6366f1)', color: 'white', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer' }}>Fazer chamada</button>
              </div>
            </div>
          </div>
          );
        })()}
      </div>

      {/* ── Attendance Detail Modal (Bottom Sheet) ── */}
      {detailSlot && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', backdropFilter: 'blur(4px)' }} onClick={() => { setDetailSlot(null); setDetailData(null); }}>
          <div style={{ background: 'var(--bg-main, #f8fafc)', borderRadius: '20px 20px 0 0', width: '100%', maxWidth: 480, maxHeight: '88vh', overflow: 'auto', boxShadow: '0 -10px 40px rgba(0,0,0,0.2)', animation: 'slideUp 0.25s ease-out' }} onClick={e => e.stopPropagation()}>
            <style>{`@keyframes slideUp { from { transform: translateY(100%); opacity: 0; } to { transform: translateY(0); opacity: 1; } }`}</style>

            {/* Navy Header (unified — handle + title + KPIs) */}
            <div style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)', padding: '0 16px 16px', color: 'white', borderRadius: '20px 20px 16px 16px' }}>
              {/* Handle bar */}
              <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 8px' }}>
                <div style={{ width: 36, height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.3)' }} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: '0.92rem', fontWeight: 800 }}>
                    {detailSlot.materia?.nome || detailSlot.numeroAula + 'a Aula'}
                  </div>
                  <div style={{ fontSize: '0.6rem', opacity: 0.75, marginTop: 2 }}>
                    {turmas.find(t => t.id === detailSlot.turmaId)?.nome} {'\u00b7'} {detailSlot.horaInicio} - {detailSlot.horaFim}
                  </div>
                </div>
                <button onClick={() => { setDetailSlot(null); setDetailData(null); }} style={{ border: 'none', background: 'rgba(255,255,255,0.15)', borderRadius: 8, width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'white' }}>
                  <span style={{ fontSize: '1rem', fontWeight: 300 }}>{'\u00d7'}</span>
                </button>
              </div>
              {/* KPIs inline — use top-level totals from Chamada model */}
              {(() => {
                const p = detailData?.totalPresentes ?? 0;
                const aus = detailData?.totalAusentes ?? 0;
                const atr = detailData?.totalAtrasados ?? 0;
                const total = detailData?.totalAlunos || (p + aus + atr);
                const pct = total > 0 ? Math.round((p / total) * 100) : 0;
                return (
                  <div style={{ display: 'flex', marginTop: 10 }}>
                    <div style={{ flex: 1, textAlign: 'center', padding: '8px 4px' }}>
                      <div style={{ fontSize: '1.1rem', fontWeight: 800 }}>{p}</div>
                      <div style={{ fontSize: '0.5rem', fontWeight: 700, opacity: 0.85, textTransform: 'uppercase' }}>Presentes</div>
                    </div>
                    <div style={{ width: 1, background: 'rgba(255,255,255,0.15)', margin: '6px 0' }} />
                    <div style={{ flex: 1, textAlign: 'center', padding: '8px 4px' }}>
                      <div style={{ fontSize: '1.1rem', fontWeight: 800 }}>{aus}</div>
                      <div style={{ fontSize: '0.5rem', fontWeight: 700, opacity: 0.85, textTransform: 'uppercase' }}>Ausentes</div>
                    </div>
                    {atr > 0 && (<>
                      <div style={{ width: 1, background: 'rgba(255,255,255,0.15)', margin: '6px 0' }} />
                      <div style={{ flex: 1, textAlign: 'center', padding: '8px 4px' }}>
                        <div style={{ fontSize: '1.1rem', fontWeight: 800 }}>{atr}</div>
                        <div style={{ fontSize: '0.5rem', fontWeight: 700, opacity: 0.85, textTransform: 'uppercase' }}>Atrasados</div>
                      </div>
                    </>)}
                    <div style={{ width: 1, background: 'rgba(255,255,255,0.15)', margin: '6px 0' }} />
                    <div style={{ flex: 1, textAlign: 'center', padding: '8px 4px' }}>
                      <div style={{ fontSize: '1.1rem', fontWeight: 800 }}>{pct}%</div>
                      <div style={{ fontSize: '0.5rem', fontWeight: 700, opacity: 0.85, textTransform: 'uppercase' }}>Frequencia</div>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Body */}
            <div style={{ padding: '12px 14px 24px' }}>
              {detailLoading && (
                <div style={{ padding: '30px 0', textAlign: 'center', fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                  Carregando detalhes...
                </div>
              )}

              {!detailLoading && detailData && !detailData._fallback && (() => {
                const alunos = detailData.alunos || [];
                const groups = [
                  { key: 'PRESENTE', title: 'Presentes', color: '#16a34a', bg: '#f0fdf4', dot: '#16a34a' },
                  { key: 'AUSENTE', title: 'Ausentes', color: '#dc2626', bg: '#fef2f2', dot: '#dc2626' },
                  { key: 'ATRASADO', title: 'Atrasados', color: '#d97706', bg: '#fffbeb', dot: '#d97706' },
                  { key: 'JUSTIFICADO', title: 'Justificados', color: '#6366f1', bg: '#f5f3ff', dot: '#6366f1' },
                  { key: 'DISPENSADO', title: 'Dispensados', color: '#6b7280', bg: '#f9fafb', dot: '#6b7280' },
                ];

                return (
                  <>
                    {groups.map(g => {
                      const items = alunos.filter((a: any) => a.statusPresenca === g.key);
                      if (items.length === 0) return null;
                      return (
                        <div key={g.key} style={{ marginBottom: 10 }}>
                          <div style={{ fontSize: '0.58rem', fontWeight: 700, color: g.color, padding: '6px 2px 4px', display: 'flex', alignItems: 'center', gap: 6, textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: g.dot, flexShrink: 0 }} />
                            {g.title} ({items.length})
                          </div>
                          <div style={{ background: 'var(--bg-card, white)', borderRadius: 10, border: '1px solid var(--border-color, #e5e7eb)', overflow: 'hidden' }}>
                            {items.map((a: any, idx: number) => (
                              <div key={a.aluno?.id || idx} style={{
                                display: 'flex', alignItems: 'center', gap: 10, padding: '7px 12px',
                                borderBottom: idx < items.length - 1 ? '1px solid var(--border-color, #f1f5f9)' : 'none',
                              }}>
                                <div style={{
                                  width: 26, height: 26, borderRadius: '50%', background: g.bg,
                                  border: '1.5px solid ' + g.color + '33',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  fontSize: '0.48rem', fontWeight: 800, color: g.color, flexShrink: 0,
                                }}>
                                  {(a.aluno?.nome || '?').split(' ').map((w: string) => w[0]).slice(0, 2).join('').toUpperCase()}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: '0.7rem', fontWeight: 500, color: 'var(--text-main, #334155)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {a.aluno?.nome || 'Aluno'}
                                  </div>
                                  {a.aluno?.matricula && (
                                    <div style={{ fontSize: '0.52rem', color: 'var(--text-muted, #94a3b8)' }}>
                                      Mat. {a.aluno.matricula}
                                    </div>
                                  )}
                                </div>
                                {a.observacoes && (
                                  <div style={{ fontSize: '0.52rem', color: '#64748b', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontStyle: 'italic' }}>
                                    {a.observacoes}
                                  </div>
                                )}
                                {a.justificativa && (
                                  <div style={{ fontSize: '0.5rem', color: '#6366f1', maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    Just: {a.justificativa}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}

                    {/* Observacoes da Chamada */}
                    {detailData.observacoes && (
                      <div style={{ marginTop: 6, background: '#f0f9ff', borderRadius: 10, border: '1px solid #bae6fd', padding: '10px 12px' }}>
                        <div style={{ fontSize: '0.58rem', fontWeight: 700, color: '#0369a1', textTransform: 'uppercase', marginBottom: 4 }}>
                          Observacoes da Chamada
                        </div>
                        <div style={{ fontSize: '0.68rem', color: '#334155', lineHeight: 1.4 }}>
                          {detailData.observacoes}
                        </div>
                      </div>
                    )}

                    {/* Diario de Classe */}
                    {detailData.diarioClasse && (
                      <div style={{ marginTop: 8, background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0', padding: '12px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <div style={{ fontSize: '0.62rem', fontWeight: 800, color: '#1e3a5f', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Diario de Classe
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                          <div style={{ background: 'white', borderRadius: 8, padding: '8px 10px', border: '1px solid #e5e7eb' }}>
                            <div style={{ fontSize: '0.5rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 2 }}>Conteudo Ministrado</div>
                            <div style={{ fontSize: '0.68rem', color: '#1e293b', lineHeight: 1.4 }}>{detailData.diarioClasse.conteudoMinistrado}</div>
                          </div>
                          {detailData.diarioClasse.metodologia && (
                            <div style={{ background: 'white', borderRadius: 8, padding: '8px 10px', border: '1px solid #e5e7eb' }}>
                              <div style={{ fontSize: '0.5rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 2 }}>Metodologia</div>
                              <div style={{ fontSize: '0.68rem', color: '#1e293b', lineHeight: 1.4 }}>{detailData.diarioClasse.metodologia}</div>
                            </div>
                          )}
                          {detailData.diarioClasse.recursosUtilizados && (
                            <div style={{ background: 'white', borderRadius: 8, padding: '8px 10px', border: '1px solid #e5e7eb' }}>
                              <div style={{ fontSize: '0.5rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 2 }}>Recursos Utilizados</div>
                              <div style={{ fontSize: '0.68rem', color: '#1e293b', lineHeight: 1.4 }}>{detailData.diarioClasse.recursosUtilizados}</div>
                            </div>
                          )}
                          {detailData.diarioClasse.observacoes && (
                            <div style={{ background: 'white', borderRadius: 8, padding: '8px 10px', border: '1px solid #e5e7eb' }}>
                              <div style={{ fontSize: '0.5rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 2 }}>Observacoes</div>
                              <div style={{ fontSize: '0.68rem', color: '#1e293b', lineHeight: 1.4 }}>{detailData.diarioClasse.observacoes}</div>
                            </div>
                          )}
                          {detailData.diarioClasse.objetivosCumpridos !== null && detailData.diarioClasse.objetivosCumpridos !== undefined && (
                            <div style={{ fontSize: '0.58rem', color: detailData.diarioClasse.objetivosCumpridos ? '#16a34a' : '#dc2626', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
                              <span style={{ fontSize: '0.7rem' }}>{detailData.diarioClasse.objetivosCumpridos ? '\u2713' : '\u2717'}</span>
                              Objetivos {detailData.diarioClasse.objetivosCumpridos ? 'cumpridos' : 'nao cumpridos'}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    {!detailData.diarioClasse && (
                      <div
                        onClick={() => setShowDiarioModal(true)}
                        style={{ marginTop: 8, background: '#fffbeb', borderRadius: 10, border: '1px solid #fde68a', padding: '12px 12px', textAlign: 'center', cursor: 'pointer', transition: 'all 0.15s' }}
                        onMouseEnter={(e: any) => { e.currentTarget.style.background = '#fef3c7'; e.currentTarget.style.transform = 'scale(1.01)'; }}
                        onMouseLeave={(e: any) => { e.currentTarget.style.background = '#fffbeb'; e.currentTarget.style.transform = 'scale(1)'; }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                          <BookOpen size={14} style={{ color: '#92400e' }} />
                          <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#92400e' }}>Preencher Diario de Classe</span>
                        </div>
                      </div>
                    )}

                    {/* Timestamp */}
                    {detailData.criadoEm && (
                      <div style={{ fontSize: '0.52rem', color: 'var(--text-muted, #94a3b8)', textAlign: 'center', marginTop: 12, paddingBottom: 8 }}>
                        Registrada em {new Date(detailData.criadoEm).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      </div>
                    )}
                  </>
                );
              })()}

              {/* ── Modal Diario de Classe ── */}
              {showDiarioModal && detailData && detailSlot && (() => {
                const chamadaInfo = chamadasDia.find((c: any) => c.horarioAulaId === detailSlot.id && c.status === 'CONCLUIDA');
                if (!chamadaInfo) return null;
                const turmaNome = turmas.find(t => t.id === detailSlot.turmaId)?.nome || '';
                const materiaNome = (detailSlot as any).materia?.nome || '';
                return (
                  <DiarioClasseModal
                    chamadaId={chamadaInfo.id}
                    professorId={user?.id || ''}
                    turmaId={detailSlot.turmaId}
                    materiaId={detailSlot.materiaId || ''}
                    turmaNome={turmaNome}
                    materiaNome={materiaNome}
                    dataReferencia={chamadaInfo.dataReferencia || dataAtual}
                    salvando={salvandoDiario}
                    onPular={() => setShowDiarioModal(false)}
                    onSalvar={async (dados: CreateDiarioClasseInput) => {
                      setSalvandoDiario(true);
                      try {
                        await api.post(ROUTES.diariosClasse.base, dados);
                        toast.success('Diario de classe registrado!');
                        setShowDiarioModal(false);
                        // Recarregar dados do detail
                        if (escolaIdStore) {
                          const { data } = await api.get(ROUTES.chamadas.findOne(escolaIdStore, chamadaInfo.id));
                          setDetailData(data);
                        }
                      } catch (err: any) {
                        toast.error(err?.response?.data?.message || 'Erro ao salvar diario');
                      } finally {
                        setSalvandoDiario(false);
                      }
                    }}
                  />
                );
              })()}

              {/* Fallback when API failed */}
              {!detailLoading && detailData?._fallback && (
                <div style={{ textAlign: 'center', padding: '16px 0' }}>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted, #94a3b8)', marginBottom: 8 }}>
                    Detalhes indisponiveis neste momento.
                  </div>
                  <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#16a34a' }}>{detailData.totalPresentes || 0}</div>
                      <div style={{ fontSize: '0.52rem', color: '#6b7280', textTransform: 'uppercase' }}>Presentes</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#dc2626' }}>{detailData.totalAusentes || 0}</div>
                      <div style={{ fontSize: '0.52rem', color: '#6b7280', textTransform: 'uppercase' }}>Ausentes</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {janelaModalData && (
        <ChamadaJanelaModal
          {...janelaModalData}
          onClose={() => setJanelaModalData(null)}
        />
      )}

      </>
    );
}